/**
 * Coordinate Grid Utility Functions
 */
import { parseInputWithLabel, pickLabel } from '../shared/inputLabels';

/**
 * Generate a unique ID for elements
 */
export const generateId = () => {
  return `el_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Convert pixel position to grid coordinate
 * @param {number} pixelX - X position in pixels relative to SVG
 * @param {number} pixelY - Y position in pixels relative to SVG
 * @param {Object} config - Grid configuration
 * @param {number} width - SVG width
 * @param {number} height - SVG height
 * @returns {{x: number, y: number}}
 */
export const pixelToGrid = (pixelX, pixelY, config, width, height) => {
  const { xMin, xMax, yMin, yMax, snapToGrid = true } = config;
  // Support separate x/y steps with backward compatibility
  const xStep = config.xStep || config.step || 1;
  const yStep = config.yStep || config.step || 1;

  // Map pixel to grid coordinate
  let gridX = xMin + (pixelX / width) * (xMax - xMin);
  let gridY = yMax - (pixelY / height) * (yMax - yMin); // Y is inverted

  // Snap to grid if enabled
  if (snapToGrid) {
    gridX = Math.round(gridX / xStep) * xStep;
    gridY = Math.round(gridY / yStep) * yStep;
  }

  // Clamp to bounds
  gridX = Math.max(xMin, Math.min(xMax, gridX));
  gridY = Math.max(yMin, Math.min(yMax, gridY));

  // Round to avoid floating point issues (precision adapts to step size)
  const xFactor = Math.pow(10, Math.max(2, Math.ceil(-Math.log10(xStep)) + 2));
  const yFactor = Math.pow(10, Math.max(2, Math.ceil(-Math.log10(yStep)) + 2));
  gridX = Math.round(gridX * xFactor) / xFactor;
  gridY = Math.round(gridY * yFactor) / yFactor;

  return { x: gridX, y: gridY };
};

/**
 * Convert grid coordinate to SVG position
 * @param {number} gridX - X coordinate on grid
 * @param {number} gridY - Y coordinate on grid
 * @param {Object} config - Grid configuration
 * @param {number} width - SVG width
 * @param {number} height - SVG height
 * @returns {{x: number, y: number}}
 */
export const gridToSvg = (gridX, gridY, config, width, height) => {
  const { xMin, xMax, yMin, yMax } = config;

  return {
    x: ((gridX - xMin) / (xMax - xMin)) * width,
    y: ((yMax - gridY) / (yMax - yMin)) * height, // Y is inverted
  };
};

/**
 * Calculate distance between two points
 * @param {{x: number, y: number}} p1
 * @param {{x: number, y: number}} p2
 * @returns {number}
 */
export const distance = (p1, p2) => {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
};

/**
 * Check if a point is near another point (within tolerance)
 * @param {{x: number, y: number}} p1
 * @param {{x: number, y: number}} p2
 * @param {number} tolerance
 * @returns {boolean}
 */
export const pointsNear = (p1, p2, tolerance = 0.5) => {
  return distance(p1, p2) <= tolerance;
};

/**
 * Find a point at or near a given position
 * @param {{x: number, y: number}} position
 * @param {Array} elements - Array of elements
 * @param {number} tolerance
 * @returns {Object|null}
 */
export const findPointAtPosition = (position, elements, tolerance = 0.5) => {
  for (const el of elements) {
    if (el.type === 'point' && pointsNear(position, el, tolerance)) {
      return el;
    }
  }
  return null;
};

/**
 * Check if a point is inside a circle
 * @param {{x: number, y: number}} point
 * @param {{center: {x: number, y: number}, radius: number}} circle
 * @returns {boolean}
 */
export const pointInCircle = (point, circle) => {
  return distance(point, circle.center) <= circle.radius;
};

/**
 * Calculate the centroid of a polygon
 * @param {Array<{x: number, y: number}>} points
 * @returns {{x: number, y: number}}
 */
export const polygonCentroid = (points) => {
  const n = points.length;
  const sum = points.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
    { x: 0, y: 0 }
  );
  return { x: sum.x / n, y: sum.y / n };
};

/**
 * Generate grid lines for rendering
 * @param {Object} config - Grid configuration
 * @returns {{vertical: Array, horizontal: Array}}
 */
export const generateGridLines = (config) => {
  const { xMin, xMax, yMin, yMax } = config;
  // Support separate x/y steps with backward compatibility
  const xStep = config.xStep || config.step || 1;
  const yStep = config.yStep || config.step || 1;
  const vertical = [];
  const horizontal = [];

  if (!xStep || xStep <= 0 || !yStep || yStep <= 0) return { vertical, horizontal };

  // Generate vertical lines (using xStep)
  for (let x = xMin; x <= xMax; x += xStep) {
    vertical.push(x);
  }

  // Generate horizontal lines (using yStep)
  for (let y = yMin; y <= yMax; y += yStep) {
    horizontal.push(y);
  }

  return { vertical, horizontal };
};

/**
 * Generate axis labels
 * @param {Object} config - Grid configuration
 * @param {number} labelInterval - Interval for labels (default: show every step)
 * @returns {{xLabels: Array, yLabels: Array}}
 */
export const generateAxisLabels = (config, labelInterval = null) => {
  const { xMin, xMax, yMin, yMax } = config;
  // Support separate x/y steps with backward compatibility
  const xStep = config.xStep || config.step || 1;
  const yStep = config.yStep || config.step || 1;
  const xInterval = labelInterval || xStep;
  const yInterval = labelInterval || yStep;
  const xLabels = [];
  const yLabels = [];

  if (!xInterval || xInterval <= 0 || !yInterval || yInterval <= 0) return { xLabels, yLabels };

  // Generate X axis labels
  for (let x = xMin; x <= xMax; x += xInterval) {
    if (x !== 0) { // Skip origin for cleaner display
      xLabels.push(x);
    }
  }

  // Generate Y axis labels
  for (let y = yMin; y <= yMax; y += yInterval) {
    if (y !== 0) { // Skip origin for cleaner display
      yLabels.push(y);
    }
  }

  return { xLabels, yLabels };
};

// Common fractions used in math education (constant for performance)
const COMMON_FRACTIONS = [
  // Halves
  { decimal: 0.5, fraction: '1/2' },
  // Thirds
  { decimal: 1/3, fraction: '1/3' },
  { decimal: 2/3, fraction: '2/3' },
  // Quarters
  { decimal: 0.25, fraction: '1/4' },
  { decimal: 0.75, fraction: '3/4' },
  // Fifths
  { decimal: 0.2, fraction: '1/5' },
  { decimal: 0.4, fraction: '2/5' },
  { decimal: 0.6, fraction: '3/5' },
  { decimal: 0.8, fraction: '4/5' },
  // Sixths
  { decimal: 1/6, fraction: '1/6' },
  { decimal: 5/6, fraction: '5/6' },
  // Eighths
  { decimal: 0.125, fraction: '1/8' },
  { decimal: 0.375, fraction: '3/8' },
  { decimal: 0.625, fraction: '5/8' },
  { decimal: 0.875, fraction: '7/8' },
  // Tenths
  { decimal: 0.1, fraction: '1/10' },
  { decimal: 0.3, fraction: '3/10' },
  { decimal: 0.7, fraction: '7/10' },
  { decimal: 0.9, fraction: '9/10' },
];

const FRACTION_EPSILON = 0.0001;

/**
 * Convert a decimal to a fraction string if it matches common fractions
 * @param {number} decimal - The decimal value to convert
 * @returns {string} - Fraction string (e.g., "1/4") or decimal string if no match
 */
export const decimalToFraction = (decimal) => {
  // Handle special cases
  if (decimal === null || decimal === undefined || isNaN(decimal)) {
    return '0';
  }

  // Handle integers
  if (Number.isInteger(decimal)) {
    return String(decimal);
  }

  // Handle negative numbers
  const isNegative = decimal < 0;
  const absDecimal = Math.abs(decimal);

  // Check whole number part
  const wholePart = Math.floor(absDecimal);
  const fractionalPart = absDecimal - wholePart;

  // If fractional part is essentially zero, return the whole number
  // Also handle "-0" case by returning "0" when wholePart is 0
  if (fractionalPart < FRACTION_EPSILON) {
    if (wholePart === 0) return '0';
    return isNegative ? `-${wholePart}` : String(wholePart);
  }

  // Find matching fraction for the fractional part
  for (const { decimal: d, fraction: f } of COMMON_FRACTIONS) {
    if (Math.abs(fractionalPart - d) < FRACTION_EPSILON) {
      const sign = isNegative ? '-' : '';
      if (wholePart === 0) {
        return `${sign}${f}`;
      }
      // Convert mixed number to improper fraction (e.g., 1 + 1/4 = 5/4)
      const [num, denom] = f.split('/').map(Number);
      const improperNum = wholePart * denom + num;
      return `${sign}${improperNum}/${denom}`;
    }
  }

  // No match found - return decimal with appropriate precision
  const formatted = parseFloat(absDecimal.toPrecision(10)).toString();
  return isNegative ? `-${formatted}` : formatted;
};

/**
 * Format coordinate for display using fractions
 * @param {number} x
 * @param {number} y
 * @returns {string}
 */
export const formatCoordinate = (x, y, xLabel, yLabel) => {
  const xStr = pickLabel(xLabel, x, decimalToFraction);
  const yStr = pickLabel(yLabel, y, decimalToFraction);
  return `(${xStr}, ${yStr})`;
};

/**
 * Get SVG path for a polygon
 * @param {Array<{x: number, y: number}>} points - Grid coordinates
 * @param {Object} config - Grid configuration
 * @param {number} width - SVG width
 * @param {number} height - SVG height
 * @returns {string}
 */
export const getPolygonPath = (points, config, width, height) => {
  if (points.length < 2) return '';

  const svgPoints = points.map((p) => gridToSvg(p.x, p.y, config, width, height));
  const pathParts = svgPoints.map((p, i) =>
    i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`
  );

  return pathParts.join(' ') + ' Z';
};

/**
 * Default grid configuration
 */
export const defaultGridConfig = {
  xMin: -10,
  xMax: 10,
  yMin: -10,
  yMax: 10,
  xStep: 1,
  yStep: 1,
  showGridLines: true,
  showAxisLabels: true,
  snapToGrid: true,
};

/**
 * Default coordinate config for questions
 */
export const defaultCoordinateConfig = {
  grid: defaultGridConfig,
  allowedModes: ['point'],
  maxElements: 20,
  precision: 2,
  // Input method configuration for students
  allowedPointInputMethods: ['click', 'type'],
  allowedLineInputMethods: ['twoPoints', 'equation'],
  allowedCurveInputMethods: ['click', 'type'],
};

/**
 * Sanitize equation input - only allow valid characters
 * @param {string} input - Raw input string
 * @returns {string} - Sanitized string with only valid characters
 */
export const sanitizeEquationInput = (input) => {
  if (!input || typeof input !== 'string') return '';
  // Allow: digits, decimal, +, -, x, y, =, spaces, ^, |, (), and letters for function names
  // Function names: sin, cos, tan, sqrt, abs, log, ln, e, pi
  return input.replace(/[^0-9.+\-xy=\s^|()a-z/]/gi, '');
};

/**
 * Parse a numeric literal that may be an integer, decimal, simple fraction,
 * or a parenthesized fraction with an optional outer sign.
 * Accepts: "3", "-3", "3.5", ".5", "3/2", "-3/4", "(3/2)", "-(3/2)"
 * @param {string} str
 * @returns {number|null} - Parsed number or null if not parseable
 */
const parseNumericLiteral = (str) => {
  if (str == null) return null;
  const s = String(str).trim();
  if (s === '') return null;

  const m = s.match(/^([+-]?)\(?([+-]?(?:\d+\.?\d*|\d*\.?\d+)(?:\/[+-]?(?:\d+\.?\d*|\d*\.?\d+))?)\)?$/);
  if (!m) return null;

  const outerSign = m[1] === '-' ? -1 : 1;
  const body = m[2];
  const slash = body.indexOf('/');
  if (slash === -1) {
    const n = parseFloat(body);
    return Number.isFinite(n) ? outerSign * n : null;
  }
  const num = parseFloat(body.slice(0, slash));
  const den = parseFloat(body.slice(slash + 1));
  if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
  return outerSign * (num / den);
};

/**
 * Parse equation string into structured form
 * Supports: y=mx+b, x=ky+c, x=c, y=c, ax+by=c (standard form)
 * @param {string} input - The equation string to parse
 * @returns {{valid: boolean, form: string, coefficient: number, constant: number, value: number, error: string}}
 */
export const parseEquation = (input) => {
  if (!input || typeof input !== 'string') {
    return { valid: false, error: 'Please enter an equation' };
  }

  // Sanitize input first - check if any characters were removed
  const sanitized = sanitizeEquationInput(input);
  if (sanitized !== input) {
    return { valid: false, error: 'Invalid characters in equation' };
  }

  // Normalize: remove spaces, convert to lowercase
  const normalized = sanitized.replace(/\s+/g, '').toLowerCase();

  // Check for basic structure
  if (!normalized.includes('=')) {
    return { valid: false, error: 'Equation must contain "="' };
  }

  // Pattern for vertical line: x = c (handles decimals like .5, 2., etc.)
  const verticalMatch = normalized.match(/^x=([+-]?(?:\d+\.?\d*|\d*\.?\d+))$/);
  if (verticalMatch) {
    const value = parseFloat(verticalMatch[1]);
    if (isNaN(value)) {
      return { valid: false, error: 'Invalid number in equation' };
    }
    return {
      valid: true,
      form: 'vertical',
      value: value,
    };
  }

  // Pattern for horizontal line: y = c
  const horizontalMatch = normalized.match(/^y=([+-]?(?:\d+\.?\d*|\d*\.?\d+))$/);
  if (horizontalMatch) {
    const value = parseFloat(horizontalMatch[1]);
    if (isNaN(value)) {
      return { valid: false, error: 'Invalid number in equation' };
    }
    return {
      valid: true,
      form: 'horizontal',
      value: value,
    };
  }

  // Pattern for y = mx + b or y = mx - b or y = mx or y = b + mx etc.
  const yFormMatch = normalized.match(/^y=(.+)$/);
  if (yFormMatch) {
    const rhs = yFormMatch[1];
    const result = parseLinearExpression(rhs, 'x');
    if (result) {
      if (result.coefficient === 0) {
        // y = b is a horizontal line
        return {
          valid: true,
          form: 'horizontal',
          value: result.constant,
        };
      }
      return {
        valid: true,
        form: 'y-form',
        coefficient: result.coefficient,
        constant: result.constant,
      };
    }
    return { valid: false, error: 'Invalid expression after "y=". Expected format: y=mx+b' };
  }

  // Pattern for x = ky + c
  const xFormMatch = normalized.match(/^x=(.+)$/);
  if (xFormMatch) {
    const rhs = xFormMatch[1];
    const result = parseLinearExpression(rhs, 'y');
    if (result) {
      if (result.coefficient === 0) {
        // x = c is a vertical line
        return {
          valid: true,
          form: 'vertical',
          value: result.constant,
        };
      }
      return {
        valid: true,
        form: 'x-form',
        coefficient: result.coefficient,
        constant: result.constant,
      };
    }
    return { valid: false, error: 'Invalid expression after "x=". Expected format: x=ky+c' };
  }

  // Try to parse standard form: ax + by = c
  const standardFormResult = parseStandardForm(normalized);
  if (standardFormResult) {
    return standardFormResult;
  }

  return { valid: false, error: 'Invalid equation format. Try: y=mx+b, x=c, or ax+by=c' };
};

/**
 * Parse standard form equation: ax + by = c
 * Converts to slope-intercept form for rendering
 * @param {string} normalized - Normalized equation string (no spaces, lowercase)
 * @returns {Object|null} - Parsed result or null if not valid standard form
 */
const parseStandardForm = (normalized) => {
  // Split by '='
  const parts = normalized.split('=');
  if (parts.length !== 2) return null;

  const [lhs, rhs] = parts;

  // Parse left side for x and y coefficients
  const leftResult = parseStandardFormLHS(lhs);
  if (!leftResult) return null;

  // Parse right side as a constant (supports fractions like 1/2 or (1/2))
  const rightValue = parseNumericLiteral(rhs);
  if (rightValue === null) return null;

  const { xCoeff, yCoeff } = leftResult;

  // ax + by = c
  // If yCoeff is 0, it's a vertical line: ax = c => x = c/a
  if (Math.abs(yCoeff) < 1e-10) {
    if (Math.abs(xCoeff) < 1e-10) {
      return null; // 0 = c is not a valid line
    }
    return {
      valid: true,
      form: 'vertical',
      value: rightValue / xCoeff,
    };
  }

  // If xCoeff is 0, it's a horizontal line: by = c => y = c/b
  if (Math.abs(xCoeff) < 1e-10) {
    return {
      valid: true,
      form: 'horizontal',
      value: rightValue / yCoeff,
    };
  }

  // General case: ax + by = c => y = (c - ax) / b => y = (-a/b)x + (c/b)
  const slope = -xCoeff / yCoeff;
  const intercept = rightValue / yCoeff;

  return {
    valid: true,
    form: 'y-form',
    coefficient: slope,
    constant: intercept,
  };
};

/**
 * Parse the left-hand side of standard form to extract x and y coefficients
 * Handles: x+2y, 2x+y, -x+3y, x-y, 3x, 2y, etc.
 * @param {string} lhs - Left-hand side expression
 * @returns {{xCoeff: number, yCoeff: number}|null}
 */
const parseStandardFormLHS = (lhs) => {
  if (!lhs) return null;

  let xCoeff = 0;
  let yCoeff = 0;
  let hasX = false;
  let hasY = false;

  // Split by + and - while keeping the sign
  const terms = lhs.match(/[+-]?[^+-]+/g);
  if (!terms) return null;

  for (const term of terms) {
    const trimmed = term.trim();
    if (!trimmed) continue;

    if (trimmed.includes('x') && trimmed.includes('y')) {
      // Invalid: term has both x and y (like "xy")
      return null;
    }

    if (trimmed.includes('x')) {
      if (hasX) return null; // Already have x term
      hasX = true;

      if (trimmed.slice(-1) !== 'x') return null;
      const coeffStr = trimmed.slice(0, -1);
      if (coeffStr === '' || coeffStr === '+') {
        xCoeff = 1;
      } else if (coeffStr === '-') {
        xCoeff = -1;
      } else {
        const parsed = parseNumericLiteral(coeffStr);
        if (parsed === null) return null;
        xCoeff = parsed;
      }
    } else if (trimmed.includes('y')) {
      if (hasY) return null; // Already have y term
      hasY = true;

      if (trimmed.slice(-1) !== 'y') return null;
      const coeffStr = trimmed.slice(0, -1);
      if (coeffStr === '' || coeffStr === '+') {
        yCoeff = 1;
      } else if (coeffStr === '-') {
        yCoeff = -1;
      } else {
        const parsed = parseNumericLiteral(coeffStr);
        if (parsed === null) return null;
        yCoeff = parsed;
      }
    } else {
      // Constant term on LHS - not supported in standard form parsing
      // (would need to move to RHS, complicating things)
      return null;
    }
  }

  // Must have at least one variable
  if (!hasX && !hasY) return null;

  return { xCoeff, yCoeff };
};

/**
 * Parse a linear expression like "2x+1", "-3x", "x", "5", "2+3x", "-x-1", ".5x", "2.x"
 * @param {string} expr - The expression to parse
 * @param {string} variable - The variable to look for ('x' or 'y')
 * @returns {{coefficient: number, constant: number}|null}
 */
const parseLinearExpression = (expr, variable) => {
  if (!expr) return null;

  let coefficient = 0;
  let constant = 0;
  let hasVariable = false;

  // Split by + and - while keeping the sign
  // Convert "2x-3" to ["2x", "-3"] and "-x+2" to ["-x", "+2"]
  const terms = expr.match(/[+-]?[^+-]+/g);
  if (!terms) return null;

  for (const term of terms) {
    const trimmed = term.trim();
    if (!trimmed) continue;

    // Check if term contains the variable
    if (trimmed.includes(variable)) {
      // Already found a variable term - can't have multiple
      if (hasVariable) return null;
      hasVariable = true;

      // Extract coefficient: term must end with the variable letter
      if (trimmed.slice(-1) !== variable) return null;
      const coeffStr = trimmed.slice(0, -1);
      if (coeffStr === '' || coeffStr === '+') {
        coefficient = 1;
      } else if (coeffStr === '-') {
        coefficient = -1;
      } else {
        const parsed = parseNumericLiteral(coeffStr);
        if (parsed === null) return null;
        coefficient = parsed;
      }
    } else {
      // Pure constant term (supports fractions like 1/2 or -(1/2))
      const num = parseNumericLiteral(trimmed);
      if (num === null) return null;
      constant += num;
    }
  }

  return { coefficient, constant };
};

/**
 * Convert parsed equation to two points for rendering
 * @param {Object} parsed - The parsed equation object
 * @param {Object} gridBounds - { xMin, xMax, yMin, yMax }
 * @returns {Array<{x: number, y: number}>} - Two points
 */
export const equationToLinePoints = (parsed, gridBounds) => {
  const { xMin, xMax, yMin, yMax } = gridBounds;

  if (parsed.form === 'vertical') {
    // x = c -> vertical line
    return [
      { x: parsed.value, y: yMin },
      { x: parsed.value, y: yMax },
    ];
  }

  if (parsed.form === 'horizontal') {
    // y = c -> horizontal line
    return [
      { x: xMin, y: parsed.value },
      { x: xMax, y: parsed.value },
    ];
  }

  if (parsed.form === 'y-form') {
    // y = mx + b
    const m = parsed.coefficient;
    const b = parsed.constant;

    // Calculate y values at xMin and xMax
    const y1 = m * xMin + b;
    const y2 = m * xMax + b;

    return [
      { x: xMin, y: y1 },
      { x: xMax, y: y2 },
    ];
  }

  if (parsed.form === 'x-form') {
    // x = ky + c
    const k = parsed.coefficient;
    const c = parsed.constant;

    if (k === 0) {
      // x = c -> vertical line
      return [
        { x: c, y: yMin },
        { x: c, y: yMax },
      ];
    }

    // Calculate x values at yMin and yMax
    const x1 = k * yMin + c;
    const x2 = k * yMax + c;

    return [
      { x: x1, y: yMin },
      { x: x2, y: yMax },
    ];
  }

  return [];
};

/**
 * Convert two-point line to slope-intercept form
 * @param {{x: number, y: number}} point1
 * @param {{x: number, y: number}} point2
 * @returns {{slope: number, intercept: number, isVertical: boolean, verticalX: number}}
 */
export const lineToSlopeIntercept = (point1, point2) => {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;

  if (Math.abs(dx) < 1e-10) {
    // Vertical line
    return { isVertical: true, verticalX: point1.x };
  }

  const slope = dy / dx;
  const intercept = point1.y - slope * point1.x;
  return { slope, intercept, isVertical: false };
};

/**
 * Check if two lines are mathematically equivalent
 * Works for both 'line' (two-point) and 'equationLine' types
 * Uses angular comparison for slopes to handle steep lines better
 * @param {Object} line1 - First line element
 * @param {Object} line2 - Second line element
 * @param {number} tolerance - Comparison tolerance
 * @returns {boolean}
 */
export const linesEquivalent = (line1, line2, tolerance = 0.01) => {
  const si1 = elementToSlopeIntercept(line1);
  const si2 = elementToSlopeIntercept(line2);

  if (!si1 || !si2) return false;

  // Both vertical
  if (si1.isVertical && si2.isVertical) {
    return Math.abs(si1.verticalX - si2.verticalX) <= tolerance;
  }

  // One vertical, one not
  if (si1.isVertical !== si2.isVertical) return false;

  // Both non-vertical: use angular comparison for slopes
  // This handles steep lines better than direct slope comparison
  // For slopes, compare angles: atan(slope1) vs atan(slope2)
  // Angular tolerance of ~0.01 radians ≈ 0.57 degrees
  const angle1 = Math.atan(si1.slope);
  const angle2 = Math.atan(si2.slope);
  const angularTolerance = tolerance; // tolerance in radians

  const slopesMatch = Math.abs(angle1 - angle2) <= angularTolerance;
  const interceptsMatch = Math.abs(si1.intercept - si2.intercept) <= tolerance;

  return slopesMatch && interceptsMatch;
};

/**
 * Convert line element to normalized slope-intercept form
 * Works for both 'line' (two-point) and 'equationLine' types
 * @param {Object} element - Line element
 * @returns {{slope: number, intercept: number, isVertical: boolean, verticalX: number}|null}
 */
export const elementToSlopeIntercept = (element) => {
  if (!element) return null;

  if (element.type === 'equationLine' && element.parsed) {
    const { form, coefficient, constant, value } = element.parsed;

    if (form === 'vertical') {
      return { isVertical: true, verticalX: value };
    }
    if (form === 'horizontal') {
      return { slope: 0, intercept: value, isVertical: false };
    }
    if (form === 'y-form') {
      // y = mx + b
      return { slope: coefficient, intercept: constant || 0, isVertical: false };
    }
    if (form === 'x-form') {
      // x = ky + c  =>  y = (x - c) / k  =>  y = (1/k)x - c/k
      if (coefficient === 0) {
        return { isVertical: true, verticalX: constant || 0 };
      }
      return {
        slope: 1 / coefficient,
        intercept: -(constant || 0) / coefficient,
        isVertical: false,
      };
    }
  }

  if ((element.type === 'line' || element.type === 'equationLine') && element.points?.length === 2) {
    return lineToSlopeIntercept(element.points[0], element.points[1]);
  }

  return null;
};

/**
 * Parse a number or fraction string into a numeric value
 * Supports: integers, decimals, fractions
 * @param {string} str - The string to parse (e.g., "1/2", "-3/4", "5", "0.5")
 * @returns {{valid: boolean, value: number, error?: string}}
 */
export const parseFraction = (str) => parseInputWithLabel(str);

/**
 * Parse coordinate input string into x and y values
 * Supports formats: "(1/2, 3/4)", "(0.5, -1)", "1, 2", "1/2,3/4"
 * @param {string} input - The coordinate string to parse
 * @returns {{valid: boolean, x: number, y: number, error?: string}}
 */
export const parseCoordinateInput = (input) => {
  if (!input || typeof input !== 'string') {
    return { valid: false, error: 'Please enter coordinates' };
  }

  // Remove parentheses and normalize whitespace
  let cleaned = input.trim();

  // Remove outer parentheses if present
  if (cleaned.startsWith('(') && cleaned.endsWith(')')) {
    cleaned = cleaned.slice(1, -1);
  }

  // Split by comma
  const parts = cleaned.split(',');
  if (parts.length !== 2) {
    return { valid: false, error: 'Enter coordinates as (x, y)' };
  }

  const xStr = parts[0].trim();
  const yStr = parts[1].trim();

  if (!xStr || !yStr) {
    return { valid: false, error: 'Both x and y values are required' };
  }

  // Parse x value
  const xResult = parseFraction(xStr);
  if (!xResult.valid) {
    return { valid: false, error: `Invalid x value: ${xResult.error}` };
  }

  // Parse y value
  const yResult = parseFraction(yStr);
  if (!yResult.valid) {
    return { valid: false, error: `Invalid y value: ${yResult.error}` };
  }

  return {
    valid: true,
    x: xResult.value,
    y: yResult.value,
    xLabel: xResult.display,
    yLabel: yResult.display,
  };
};

// ============================================
// CURVE SUPPORT FUNCTIONS
// ============================================

/**
 * Curve types supported by the system
 */
export const CURVE_TYPES = {
  QUADRATIC: 'quadratic',
  ABSOLUTE: 'absolute',
  SQRT: 'sqrt',
  NTH_ROOT: 'nthRoot',
  EXPONENTIAL: 'exponential',
  LOGARITHMIC: 'logarithmic',
  SINE: 'sine',
  COSINE: 'cosine',
  TANGENT: 'tangent',
  POLYNOMIAL: 'polynomial',
};

/**
 * Points required for each curve type when fitting from clicks
 */
export const CURVE_POINTS_REQUIRED = {
  [CURVE_TYPES.QUADRATIC]: 3,
  [CURVE_TYPES.ABSOLUTE]: 2,
  [CURVE_TYPES.SQRT]: 2,
  [CURVE_TYPES.NTH_ROOT]: 2,
  [CURVE_TYPES.EXPONENTIAL]: 3,
  [CURVE_TYPES.LOGARITHMIC]: 2,
  [CURVE_TYPES.SINE]: 3,
  [CURVE_TYPES.COSINE]: 3,
  [CURVE_TYPES.TANGENT]: 3,
};

/**
 * Check if a mode is a curve mode (quadratic, absolute, sqrt, nthRoot, exponential, logarithmic, sine, cosine, tangent, polynomial*)
 * @param {string} mode - The mode string
 * @returns {boolean}
 */
export const isCurveMode = (mode) => {
  if (!mode) return false;
  const curveModes = ['quadratic', 'absolute', 'sqrt', 'nthRoot', 'exponential', 'logarithmic', 'sine', 'cosine', 'tangent'];
  return curveModes.includes(mode) || mode.startsWith('polynomial');
};

/**
 * Get the number of points required for any curve mode
 * Wraps CURVE_POINTS_REQUIRED for standard curves and polynomial logic for polynomial modes
 * @param {string} mode - The curve mode string
 * @returns {number} - Number of points required, or 0 if not a curve mode
 */
export const getCurvePointsRequired = (mode) => {
  if (!mode) return 0;
  if (CURVE_POINTS_REQUIRED[mode]) return CURVE_POINTS_REQUIRED[mode];
  if (mode.startsWith('polynomial')) {
    const degree = parseInt(mode.replace('polynomial', ''), 10);
    if (!isNaN(degree) && degree >= 1) return getPolynomialPointsRequired(degree);
  }
  return 0;
};

/**
 * Get the number of points required for a polynomial of given degree
 * @param {number} degree - Polynomial degree
 * @returns {number} - Number of points required (degree + 1)
 */
export const getPolynomialPointsRequired = (degree) => degree + 1;

/**
 * Multiply two polynomials represented as coefficient arrays
 * Coefficients are in ascending power order: [a₀, a₁, a₂, ...] = a₀ + a₁x + a₂x² + ...
 * @param {number[]} p1 - First polynomial coefficients
 * @param {number[]} p2 - Second polynomial coefficients
 * @returns {number[]} - Product polynomial coefficients
 */
const multiplyPolynomials = (p1, p2) => {
  const result = new Array(p1.length + p2.length - 1).fill(0);
  for (let i = 0; i < p1.length; i++) {
    for (let j = 0; j < p2.length; j++) {
      result[i + j] += p1[i] * p2[j];
    }
  }
  return result;
};

/**
 * Compute standard form coefficients from Lagrange interpolation
 * @param {Array<{x: number, y: number}>} points - N+1 points for degree N polynomial
 * @returns {number[]} Coefficients [a₀, a₁, ..., aₙ] in ascending power order
 */
const computeLagrangeCoefficients = (points) => {
  const n = points.length;

  // Initialize result coefficients to zero
  const result = new Array(n).fill(0);

  // For each point, compute its Lagrange basis polynomial and add contribution
  for (let i = 0; i < n; i++) {
    const yi = points[i].y;
    const xi = points[i].x;

    // Compute L_i(x) = product of (x - x_j) / (x_i - x_j) for j != i
    // Start with [1] (constant polynomial = 1)
    let basis = [1];
    let denominator = 1;

    for (let j = 0; j < n; j++) {
      if (j === i) continue;

      const xj = points[j].x;
      denominator *= (xi - xj);

      // Multiply basis by (x - xj) = [-xj, 1]
      basis = multiplyPolynomials(basis, [-xj, 1]);
    }

    // Scale by yi / denominator and add to result
    const scale = yi / denominator;
    for (let k = 0; k < basis.length; k++) {
      result[k] += basis[k] * scale;
    }
  }

  // Round to avoid floating point noise
  return result.map(c => Math.round(c * 1e10) / 1e10);
};

/**
 * Fit a polynomial of degree N through N+1 points using Lagrange interpolation
 * @param {Array<{x: number, y: number}>} points - Exactly N+1 points for degree N
 * @returns {{valid: boolean, type: string, params: Object, error: string}}
 */
export const fitPolynomial = (points) => {
  if (!points || points.length < 2) {
    return { valid: false, error: 'At least 2 points required' };
  }

  const n = points.length;
  const degree = n - 1;

  // Check for duplicate x values
  const xValues = points.map(p => p.x);
  const uniqueX = new Set(xValues);
  if (uniqueX.size !== n) {
    return { valid: false, error: 'All points must have different x values' };
  }

  // Compute coefficients using Lagrange interpolation
  const coefficients = computeLagrangeCoefficients(points);

  if (!coefficients) {
    return { valid: false, error: 'Could not compute polynomial coefficients' };
  }

  return {
    valid: true,
    type: CURVE_TYPES.POLYNOMIAL,
    params: {
      degree,
      coefficients,
    },
  };
};

/**
 * Format polynomial coefficients as a readable equation string
 * @param {number[]} coefficients - [a₀, a₁, ..., aₙ] in ascending power order
 * @returns {string} - e.g., "y = 2x³ - 3x² + x - 1"
 */
export const formatPolynomialEquation = (coefficients) => {
  if (!coefficients || coefficients.length === 0) return 'y = 0';

  const superscripts = ['', '', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹', '¹⁰'];
  const terms = [];

  // Build terms from highest to lowest degree
  for (let i = coefficients.length - 1; i >= 0; i--) {
    const coef = coefficients[i];
    if (Math.abs(coef) < 1e-10) continue;

    const absCoef = Math.abs(coef);
    // Format coefficient: hide "1" for x terms, show decimals nicely
    let coefStr;
    if (absCoef === 1 && i > 0) {
      coefStr = '';
    } else if (Number.isInteger(absCoef)) {
      coefStr = absCoef.toString();
    } else {
      coefStr = absCoef.toFixed(2).replace(/\.?0+$/, '');
    }

    let term = '';
    if (i === 0) {
      term = coefStr || '1';
    } else if (i === 1) {
      term = `${coefStr}x`;
    } else {
      const exp = superscripts[i] || `^${i}`;
      term = `${coefStr}x${exp}`;
    }

    if (terms.length === 0) {
      terms.push(coef < 0 ? `-${term}` : term);
    } else {
      terms.push(coef < 0 ? ` - ${term}` : ` + ${term}`);
    }
  }

  return `y = ${terms.join('') || '0'}`;
};

/**
 * Parse a curve equation string
 * Supports: y = ax^2 + bx + c, y = |x|, y = sqrt(x), y = 2^x, y = log(x), y = sin(x), y = cos(x)
 * @param {string} input - The equation string
 * @returns {{valid: boolean, type: string, params: Object, error: string}}
 */
export const parseCurveEquation = (input) => {
  if (!input || typeof input !== 'string') {
    return { valid: false, error: 'Please enter an equation' };
  }

  // Normalize: remove spaces, convert to lowercase
  const normalized = input.replace(/\s+/g, '').toLowerCase();

  // Must have y= format
  if (!normalized.startsWith('y=')) {
    return { valid: false, error: 'Equation must start with "y="' };
  }

  const rhs = normalized.slice(2); // Everything after "y="

  // Try each parser in order
  let result;

  // 1. Try quadratic (must check before linear since it contains x)
  result = parseQuadraticExpression(rhs);
  if (result.valid) return result;

  // 2. Try absolute value
  result = parseAbsoluteExpression(rhs);
  if (result.valid) return result;

  // 3. Try square root
  result = parseSqrtExpression(rhs);
  if (result.valid) return result;

  // 3b. Try nth-root (cbrt, root(n,...), nthroot(n,...), ∛, (...)^(1/n))
  result = parseNthRootExpression(rhs);
  if (result.valid) return result;

  // 4. Try exponential
  result = parseExponentialExpression(rhs);
  if (result.valid) return result;

  // 5. Try logarithmic
  result = parseLogarithmicExpression(rhs);
  if (result.valid) return result;

  // 6. Try sine
  result = parseSineExpression(rhs);
  if (result.valid) return result;

  // 7. Try cosine
  result = parseCosineExpression(rhs);
  if (result.valid) return result;

  // 8. Try tangent
  result = parseTangentExpression(rhs);
  if (result.valid) return result;

  return { valid: false, error: 'Could not parse curve equation' };
};

/**
 * Parse quadratic expression: ax^2 + bx + c
 * Matches: x^2, 2x^2, -x^2, x^2+2x+1, 2x^2-3x+1, etc.
 */
const parseQuadraticExpression = (expr) => {
  // Check if expression contains x^2
  if (!expr.includes('x^2')) {
    return { valid: false };
  }

  let a = 0, b = 0, c = 0;

  // Split into terms, keeping signs
  const terms = expr.match(/[+-]?[^+-]+/g) || [];

  for (const term of terms) {
    const t = term.trim();
    if (!t) continue;

    if (t.includes('x^2')) {
      // Coefficient of x^2
      const match = t.match(/^([+-]?[\d.]*)?x\^2$/);
      if (match) {
        const coefStr = match[1];
        if (coefStr === '' || coefStr === '+') a = 1;
        else if (coefStr === '-') a = -1;
        else a = parseFloat(coefStr);
      } else {
        return { valid: false };
      }
    } else if (t.includes('x')) {
      // Coefficient of x (linear term)
      const match = t.match(/^([+-]?[\d.]*)?x$/);
      if (match) {
        const coefStr = match[1];
        if (coefStr === '' || coefStr === '+') b = 1;
        else if (coefStr === '-') b = -1;
        else b = parseFloat(coefStr);
      } else {
        return { valid: false };
      }
    } else {
      // Constant term
      const num = parseFloat(t);
      if (!isNaN(num)) {
        c = num;
      } else {
        return { valid: false };
      }
    }
  }

  if (isNaN(a) || isNaN(b) || isNaN(c)) {
    return { valid: false };
  }

  return {
    valid: true,
    type: CURVE_TYPES.QUADRATIC,
    params: { a, b, c },
  };
};

/**
 * Parse absolute value expression: a|x - h| + k
 * Matches: |x|, |x-2|, 2|x|, |x|+1, 2|x-3|+4, -|x+1|-2
 */
const parseAbsoluteExpression = (expr) => {
  // Must contain | ... |
  const absMatch = expr.match(/^([+-]?[\d.]*)?(?:\|([^|]+)\|)([+-][\d.]+)?$/);
  if (!absMatch) {
    return { valid: false };
  }

  let a = 1, h = 0, k = 0;

  // Coefficient before |...|
  const aStr = absMatch[1];
  if (aStr === '' || aStr === '+' || aStr === undefined) a = 1;
  else if (aStr === '-') a = -1;
  else a = parseFloat(aStr);

  // Inside the absolute value: x - h or x + h
  const inside = absMatch[2];
  const insideMatch = inside.match(/^x([+-][\d.]+)?$/);
  if (insideMatch) {
    // x or x+2 or x-3
    if (insideMatch[1]) {
      // h is negated: |x - 2| means h = 2
      h = -parseFloat(insideMatch[1]);
    }
  } else {
    return { valid: false };
  }

  // Constant after |...|
  if (absMatch[3]) {
    k = parseFloat(absMatch[3]);
  }

  if (isNaN(a) || isNaN(h) || isNaN(k)) {
    return { valid: false };
  }

  return {
    valid: true,
    type: CURVE_TYPES.ABSOLUTE,
    params: { a, h, k },
  };
};

/**
 * Parse square root expression: a*sqrt(b*(x - h)) + k
 * Matches: sqrt(x), 2sqrt(x), sqrt(x-1), sqrt(x)+2, 2sqrt(x-3)+1,
 *          sqrt(-x), sqrt(-x+4), 2sqrt(-x-1)+3 (left-opening)
 * `b` is ±1: +1 opens to the right (domain x ≥ h), −1 opens to the left (x ≤ h).
 */
const parseSqrtExpression = (expr) => {
  // Must contain sqrt(...)
  const sqrtMatch = expr.match(/^([+-]?[\d.]*)?sqrt\(([^)]+)\)([+-][\d.]+)?$/);
  if (!sqrtMatch) {
    return { valid: false };
  }

  let a = 1, b = 1, h = 0, k = 0;

  // Coefficient before sqrt
  const aStr = sqrtMatch[1];
  if (aStr === '' || aStr === '+' || aStr === undefined) a = 1;
  else if (aStr === '-') a = -1;
  else a = parseFloat(aStr);

  // Inside sqrt: (±)x (± constant)? — supports both x-forms and reflected -x-forms
  const inside = sqrtMatch[2];
  const insideMatch = inside.match(/^([+-]?)x([+-][\d.]+)?$/);
  if (!insideMatch) {
    return { valid: false };
  }
  b = insideMatch[1] === '-' ? -1 : 1;
  // inside = b*x + c = b*(x - (-c/b)) = b*(x - h)   →   h = -c*b  (b² = 1)
  if (insideMatch[2]) {
    const c = parseFloat(insideMatch[2]);
    h = -c * b;
  }

  // Constant after sqrt(...)
  if (sqrtMatch[3]) {
    k = parseFloat(sqrtMatch[3]);
  }

  if (isNaN(a) || isNaN(h) || isNaN(k)) {
    return { valid: false };
  }

  return {
    valid: true,
    type: CURVE_TYPES.SQRT,
    params: { a, b, h, k },
  };
};

/**
 * Parse nth-root expression: a * ⁿ√(x - h) + k
 * Accepts:
 *   cbrt(inside), 2cbrt(x-1)+3, -cbrt(x)         → n = 3
 *   ∛(inside), 2∛(x)+1                            → n = 3
 *   root(n, inside), nthroot(n, inside)           → any integer n ≥ 2
 *   (inside)^(1/n)                                → any integer n ≥ 2
 *   \sqrt[n]{inside}                              → LaTeX nth-root
 */
const parseNthRootExpression = (expr) => {
  let n = null;
  let aStr = null;
  let insideRaw = null;
  let kStr = null;

  // Form 1: cbrt(inside) or ∛(inside) → n = 3
  let m = expr.match(/^([+-]?[\d.]*)?(?:cbrt|∛)\(([^)]+)\)([+-][\d.]+)?$/);
  if (m) {
    n = 3;
    aStr = m[1];
    insideRaw = m[2];
    kStr = m[3];
  }

  // Form 2: root(n, inside) or nthroot(n, inside)
  if (!n) {
    m = expr.match(/^([+-]?[\d.]*)?(?:nthroot|root)\((\d+)\s*,\s*([^)]+)\)([+-][\d.]+)?$/);
    if (m) {
      n = parseInt(m[2], 10);
      aStr = m[1];
      insideRaw = m[3];
      kStr = m[4];
    }
  }

  // Form 3: (inside)^(1/n)
  if (!n) {
    m = expr.match(/^([+-]?[\d.]*)?\(([^)]+)\)\^\(1\/(\d+)\)([+-][\d.]+)?$/);
    if (m) {
      n = parseInt(m[3], 10);
      aStr = m[1];
      insideRaw = m[2];
      kStr = m[4];
    }
  }

  // Form 4: \sqrt[n]{inside} — LaTeX notation (renders cleanly in KaTeX).
  // parseCurveEquation strips whitespace + lowercases but leaves the
  // backslash intact, so the input arrives as "\sqrt[3]{x-1}" etc.
  if (!n) {
    m = expr.match(/^([+-]?[\d.]*)?\\sqrt\[(\d+)\]\{([^}]+)\}([+-][\d.]+)?$/);
    if (m) {
      n = parseInt(m[2], 10);
      aStr = m[1];
      insideRaw = m[3];
      kStr = m[4];
    }
  }

  if (!n || n < 2) {
    return { valid: false };
  }

  // Coefficient a
  let a = 1;
  if (aStr === '' || aStr === '+' || aStr == null) a = 1;
  else if (aStr === '-') a = -1;
  else a = parseFloat(aStr);

  // Inside must be of the form `x`, `x+c`, or `x-c`
  let h = 0;
  const insideMatch = insideRaw.trim().match(/^x([+-][\d.]+)?$/);
  if (!insideMatch) {
    return { valid: false };
  }
  if (insideMatch[1]) {
    h = -parseFloat(insideMatch[1]);
  }

  // Trailing constant
  let k = 0;
  if (kStr) {
    k = parseFloat(kStr);
  }

  if (isNaN(a) || isNaN(h) || isNaN(k)) {
    return { valid: false };
  }

  return {
    valid: true,
    type: CURVE_TYPES.NTH_ROOT,
    params: { n, a, h, k },
  };
};

/**
 * Parse exponential expression: a * b^x + c
 * Supports multiple notation styles:
 *   Pattern 1: base^x           — 2^x, e^x, 3*2^x, 2^x+1
 *   Pattern 2: base^(x±h)       — 4*2^(x-1)-3, 2^(x-3)
 *   Pattern 3: (n/d)^x          — (1/2)^x, 3*(2/3)^x
 *   Pattern 4: (n/d)^(x±h)      — (1/4)^(x-1)+1, 5*(2/3)^(x+1)-2
 *   Pattern 5: base^(-x)        — 2*4^(-x), 4^-x
 *   Pattern 6: e^(kx)           — 3e^(0.25x), 0.5e^(-x)
 *   Pattern 7: e^(k(x±h))       — e^(-0.75(x-2))+1
 */
const parseExponentialExpression = (expr) => {
  // Helper: parse coefficient string (shared across all patterns)
  const parseCoeff = (str) => {
    if (str === '' || str === '+' || str === undefined) return 1;
    if (str === '-') return -1;
    return parseFloat(str);
  };

  // Helper: parse base string (number or 'e')
  const parseBase = (str) => str === 'e' ? Math.E : parseFloat(str);

  // Helper: validate and return result
  const makeResult = (a, base, c) => {
    if (!isNaN(a) && !isNaN(base) && !isNaN(c) && base > 0) {
      return { valid: true, type: CURVE_TYPES.EXPONENTIAL, params: { a, base, c } };
    }
    return null;
  };

  // Helper: calculate coefficient from separate sign and numeric parts
  // Used by patterns that require * separator to avoid decimal base ambiguity
  const calcA = (sign, coeff) => {
    const s = sign === '-' ? -1 : 1;
    return s * (coeff ? parseFloat(coeff) : 1);
  };

  // Pattern 1: base^x — e.g., 2^x, e^x, 3*2^x, 0.9^x, 2^x+1, 3*2^x-2
  // Uses * separator to disambiguate coefficient from decimal base
  const expMatch = expr.match(/^([+-])?(?:([\d.]+)\*)?([e\d.]+)\^x([+-][\d.]+)?$/);
  if (expMatch) {
    const a = calcA(expMatch[1], expMatch[2]);
    const base = parseBase(expMatch[3]);
    const c = expMatch[4] ? parseFloat(expMatch[4]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 2: base^(x±h) — e.g., 4*2^(x-1)-3, 2^(x-3), 0.9^(x-1)
  const shiftMatch = expr.match(/^([+-])?(?:([\d.]+)\*)?([e\d.]+)\^\(x([+-][\d.]+)\)([+-][\d.]+)?$/);
  if (shiftMatch) {
    let a = calcA(shiftMatch[1], shiftMatch[2]);
    const base = parseBase(shiftMatch[3]);
    const h = parseFloat(shiftMatch[4]);
    if (!isNaN(h)) a = a * Math.pow(base, h);
    const c = shiftMatch[5] ? parseFloat(shiftMatch[5]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 3: (n/d)^x — e.g., (1/2)^x, 3*(2/3)^x, -3*(3/8)^x
  const fracMatch = expr.match(/^([+-]?[\d.]*)?(?:\*)?\((\d+)\/(\d+)\)\^x([+-][\d.]+)?$/);
  if (fracMatch) {
    const a = parseCoeff(fracMatch[1]);
    const base = parseInt(fracMatch[2], 10) / parseInt(fracMatch[3], 10);
    const c = fracMatch[4] ? parseFloat(fracMatch[4]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 4: (n/d)^(x±h) — e.g., (1/4)^(x-1)+1, 5*(2/3)^(x+1)-2
  const fracShiftMatch = expr.match(/^([+-]?[\d.]*)?(?:\*)?\((\d+)\/(\d+)\)\^\(x([+-][\d.]+)\)([+-][\d.]+)?$/);
  if (fracShiftMatch) {
    let a = parseCoeff(fracShiftMatch[1]);
    const base = parseInt(fracShiftMatch[2], 10) / parseInt(fracShiftMatch[3], 10);
    const h = parseFloat(fracShiftMatch[4]);
    if (!isNaN(h)) a = a * Math.pow(base, h);
    const c = fracShiftMatch[5] ? parseFloat(fracShiftMatch[5]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 4b: (n/d)*base^x — e.g., (1/2)*4^x, -(3/4)*2^x+1
  const fracCoeffMatch = expr.match(/^([+-])?\((\d+)\/(\d+)\)\*([e\d.]+)\^x([+-][\d.]+)?$/);
  if (fracCoeffMatch) {
    const sign = fracCoeffMatch[1] === '-' ? -1 : 1;
    const a = sign * (parseInt(fracCoeffMatch[2], 10) / parseInt(fracCoeffMatch[3], 10));
    const base = parseBase(fracCoeffMatch[4]);
    const c = fracCoeffMatch[5] ? parseFloat(fracCoeffMatch[5]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 4c: (n/d)*base^(x±h) — e.g., (1/2)*4^(x-1)+3
  const fracCoeffShiftMatch = expr.match(/^([+-])?\((\d+)\/(\d+)\)\*([e\d.]+)\^\(x([+-][\d.]+)\)([+-][\d.]+)?$/);
  if (fracCoeffShiftMatch) {
    const sign = fracCoeffShiftMatch[1] === '-' ? -1 : 1;
    let a = sign * (parseInt(fracCoeffShiftMatch[2], 10) / parseInt(fracCoeffShiftMatch[3], 10));
    const base = parseBase(fracCoeffShiftMatch[4]);
    const h = parseFloat(fracCoeffShiftMatch[5]);
    if (!isNaN(h)) a = a * Math.pow(base, h);
    const c = fracCoeffShiftMatch[6] ? parseFloat(fracCoeffShiftMatch[6]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 4d: (n/d)*base^(-x) — e.g., (1/2)*4^(-x)
  const fracCoeffNegMatch = expr.match(/^([+-])?\((\d+)\/(\d+)\)\*([e\d.]+)\^\(?-x\)?([+-][\d.]+)?$/);
  if (fracCoeffNegMatch) {
    const sign = fracCoeffNegMatch[1] === '-' ? -1 : 1;
    const a = sign * (parseInt(fracCoeffNegMatch[2], 10) / parseInt(fracCoeffNegMatch[3], 10));
    const base = 1 / parseBase(fracCoeffNegMatch[4]);
    const c = fracCoeffNegMatch[5] ? parseFloat(fracCoeffNegMatch[5]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 5: base^(-x) — e.g., 2*4^(-x), 4^-x, 4^(-x)+1, 0.9^(-x)
  const negExpMatch = expr.match(/^([+-])?(?:([\d.]+)\*)?([e\d.]+)\^\(?-x\)?([+-][\d.]+)?$/);
  if (negExpMatch) {
    const a = calcA(negExpMatch[1], negExpMatch[2]);
    const originalBase = parseBase(negExpMatch[3]);
    const base = 1 / originalBase; // Invert: b^(-x) = (1/b)^x
    const c = negExpMatch[4] ? parseFloat(negExpMatch[4]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 5b: (decimal)^x — e.g., 4200(0.9)^x, (0.9)^x, -4200(0.9)^x, 4200*(0.9)^x
  const parenDecMatch = expr.match(/^([+-])?([\d.]+)?(?:\*)?\(([\d.]+)\)\^x([+-][\d.]+)?$/);
  if (parenDecMatch) {
    const a = calcA(parenDecMatch[1], parenDecMatch[2]);
    const base = parseFloat(parenDecMatch[3]);
    const c = parenDecMatch[4] ? parseFloat(parenDecMatch[4]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 5c: (decimal)^(x±h) — e.g., 4200(0.9)^(x-1)+5
  const parenDecShiftMatch = expr.match(/^([+-])?([\d.]+)?(?:\*)?\(([\d.]+)\)\^\(x([+-][\d.]+)\)([+-][\d.]+)?$/);
  if (parenDecShiftMatch) {
    let a = calcA(parenDecShiftMatch[1], parenDecShiftMatch[2]);
    const base = parseFloat(parenDecShiftMatch[3]);
    const h = parseFloat(parenDecShiftMatch[4]);
    if (!isNaN(h)) a = a * Math.pow(base, h);
    const c = parenDecShiftMatch[5] ? parseFloat(parenDecShiftMatch[5]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 5d: (decimal)^(-x) — e.g., 4200(0.9)^(-x), (0.9)^-x
  const parenDecNegMatch = expr.match(/^([+-])?([\d.]+)?(?:\*)?\(([\d.]+)\)\^\(?-x\)?([+-][\d.]+)?$/);
  if (parenDecNegMatch) {
    const a = calcA(parenDecNegMatch[1], parenDecNegMatch[2]);
    const originalBase = parseFloat(parenDecNegMatch[3]);
    const base = 1 / originalBase;
    const c = parenDecNegMatch[4] ? parseFloat(parenDecNegMatch[4]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 6: e^(kx) — e.g., 3e^(0.25x), 2e^(0.5x), 0.5e^(-x)+1
  const eRateMatch = expr.match(/^([+-]?[\d.]*)?(?:\*)?e\^\(([+-]?[\d.]+)x\)([+-][\d.]+)?$/);
  if (eRateMatch) {
    const a = parseCoeff(eRateMatch[1]);
    const k = parseFloat(eRateMatch[2]);
    const base = Math.pow(Math.E, k); // e^(kx) = (e^k)^x
    const c = eRateMatch[3] ? parseFloat(eRateMatch[3]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  // Pattern 7: e^(k(x±h)) — e.g., e^(-0.75(x-2))+1, 1.5e^(0.25(x-1))-2
  const eRateShiftMatch = expr.match(/^([+-]?[\d.]*)?(?:\*)?e\^\(([+-]?[\d.]+)\(x([+-][\d.]+)\)\)([+-][\d.]+)?$/);
  if (eRateShiftMatch) {
    let a = parseCoeff(eRateShiftMatch[1]);
    const k = parseFloat(eRateShiftMatch[2]);
    const base = Math.pow(Math.E, k);
    const h = parseFloat(eRateShiftMatch[3]);
    if (!isNaN(h)) a = a * Math.pow(base, h);
    const c = eRateShiftMatch[4] ? parseFloat(eRateShiftMatch[4]) : 0;
    const r = makeResult(a, base, c);
    if (r) return r;
  }

  return { valid: false };
};

/**
 * Parse logarithmic expression: a * log(x - h) + k or a * ln(x - h) + k
 * Matches: log(x), ln(x), 2log(x), log(x-1), log(x)+2
 */
const parseLogarithmicExpression = (expr) => {
  // Supported formats:
  //   ln(x+3)          → base e
  //   log(x-2)+1       → base 10
  //   log_2(x+3)+1     → base 2
  //   log2(x+3)        → base 2 (shorthand)
  //   log_(1/3)(x-3)   → base 1/3 (fraction in parens)
  //   log_{1/3}(x-3)   → base 1/3 (fraction in curly braces, LaTeX-style)
  //   log_{2}(x+3)     → base 2 (curly braces)
  //   2log_5(x)        → coefficient 2, base 5
  //   -3*log_4(x+1)-2  → coefficient -3, base 4, shift -2

  // Match: [coeff] (ln | log[_base]) ( inner ) [±k]
  // Base can be: _N, _N.N, _(N/N), _{N/N}, or just N after "log" (shorthand like log2)
  // Supports both () and {} as base delimiters
  const logMatch = expr.match(
    /^([+-]?[\d.]*)?(?:\*)?(ln|log(?:_[({]?([\d.]+(?:\/[\d.]+)?)[)}]?|([\d.]+))?)?\(([^)]+)\)([+-][\d.]+)?$/
  );
  if (!logMatch) {
    return { valid: false };
  }

  let a = 1, base = 10, h = 0, k = 0;

  // Coefficient before log/ln
  const aStr = logMatch[1];
  if (aStr === '' || aStr === '+' || aStr === undefined) a = 1;
  else if (aStr === '-') a = -1;
  else a = parseFloat(aStr);

  // Determine base
  const funcName = logMatch[2]; // e.g. "ln", "log", "log_2", "log_(1/3)", "log5"
  const baseSubscript = logMatch[3]; // from _N or _(N/N)
  const baseShorthand = logMatch[4]; // from logN (no underscore)

  if (funcName === 'ln') {
    base = Math.E;
  } else if (baseSubscript) {
    // Handle fractions like "1/3"
    if (baseSubscript.includes('/')) {
      const [num, den] = baseSubscript.split('/');
      base = parseFloat(num) / parseFloat(den);
    } else {
      base = parseFloat(baseSubscript);
    }
  } else if (baseShorthand) {
    base = parseFloat(baseShorthand);
  } else {
    base = 10; // plain "log"
  }

  if (isNaN(base) || base <= 0 || base === 1) {
    return { valid: false };
  }

  // Inside log: x - h or x + h or just x
  const inside = logMatch[5];
  const insideMatch = inside.match(/^x([+-][\d.]+)?$/);
  if (insideMatch) {
    if (insideMatch[1]) {
      h = -parseFloat(insideMatch[1]);
    }
  } else {
    return { valid: false };
  }

  // Constant after log(...)
  if (logMatch[6]) {
    k = parseFloat(logMatch[6]);
  }

  if (isNaN(a) || isNaN(h) || isNaN(k)) {
    return { valid: false };
  }

  return {
    valid: true,
    type: CURVE_TYPES.LOGARITHMIC,
    params: { a, base, h, k },
  };
};

// =====================================================================
// Pi-aware helpers shared by sine / cosine / tangent expression parsers.
// Accept numeric, fractional, and pi-based coefficients (e.g. "1/2",
// "pi/2", "2pi/3", "(1/2)", "(pi/2)"). Kept local to this file on
// purpose — trigGraph has a similar implementation but cross-component
// dependencies are discouraged here.
// =====================================================================

/**
 * Parse a plain fraction or decimal string to a number.
 * Returns NaN if unparseable. Handles "2", "-3", "0.5", "1/2", "-3/4".
 */
const _parsePlainFraction = (str) => {
  if (typeof str !== 'string') return NaN;
  const s = str.trim();
  if (!s) return NaN;
  if (s.includes('/')) {
    const parts = s.split('/');
    if (parts.length !== 2) return NaN;
    const num = parseFloat(parts[0]);
    const den = parseFloat(parts[1]);
    if (isNaN(num) || isNaN(den) || den === 0) return NaN;
    return num / den;
  }
  const n = parseFloat(s);
  return isNaN(n) ? NaN : n;
};

/**
 * Strip balanced outer parentheses, handling an optional leading sign.
 * "(1/2)" → "1/2", "-(pi/2)" → "-pi/2", "((x))" → "x".
 */
const _stripOuterParens = (str) => {
  let s = (str || '').trim();
  // Peel a leading sign off so "-(1/2)" gets its parens stripped too
  let sign = '';
  if (s.startsWith('+') || s.startsWith('-')) {
    sign = s[0];
    s = s.slice(1).trim();
  }
  while (s.startsWith('(') && s.endsWith(')')) {
    let depth = 0;
    let wraps = true;
    for (let i = 0; i < s.length; i++) {
      if (s[i] === '(') depth++;
      else if (s[i] === ')') {
        depth--;
        if (depth === 0 && i < s.length - 1) { wraps = false; break; }
      }
    }
    if (!wraps) break;
    s = s.slice(1, -1).trim();
  }
  return sign + s;
};

/**
 * Parse a pi-aware numeric string. Accepts:
 *   "2", "-3", "1/2", "-3/4", "(1/2)", "-(1/2)"
 *   "pi", "π", "-pi", "2pi", "-3pi", "(1/2)pi"
 *   "pi/2", "3pi/4", "-2pi/3", "(pi/2)"
 * Returns NaN for anything it cannot parse.
 */
export const parsePiNumeric = (rawStr) => {
  if (typeof rawStr !== 'string') return NaN;
  let s = rawStr.trim().replace(/π/g, 'pi');
  if (!s) return NaN;
  s = _stripOuterParens(s);
  if (!s) return NaN;
  if (!s.includes('pi')) {
    return _parsePlainFraction(s);
  }
  // Split on "pi" — must produce exactly two parts (coeff, remainder).
  const parts = s.split('pi');
  if (parts.length !== 2) return NaN;
  let coeffStr = _stripOuterParens(parts[0]).trim();
  const remainderStr = parts[1].trim();

  // Parse the coefficient in front of pi.
  let coeff;
  if (coeffStr === '' || coeffStr === '+') coeff = 1;
  else if (coeffStr === '-') coeff = -1;
  else {
    coeff = _parsePlainFraction(coeffStr);
    if (isNaN(coeff)) return NaN;
  }

  // Parse the remainder after pi (must be empty or "/N").
  let denom = 1;
  if (remainderStr === '') {
    denom = 1;
  } else if (remainderStr.startsWith('/')) {
    denom = _parsePlainFraction(remainderStr.slice(1));
    if (isNaN(denom) || denom === 0) return NaN;
  } else {
    return NaN;
  }

  return (coeff * Math.PI) / denom;
};

/**
 * Parse the argument of a trig function of the form "Bx + C".
 * Accepts pi-based coefficients, e.g. "pix/2", "(pi/2)x - pi/4",
 * "2x + pi/3", "-x".
 * Returns { valid: boolean, B?: number, C?: number }.
 */
const parseTrigArgument = (rawArg) => {
  if (typeof rawArg !== 'string') return { valid: false };
  let s = rawArg.trim().replace(/π/g, 'pi');
  // Strip optional multiplication asterisk between coefficient and x ("pi*x" → "pix", "2*x" → "2x")
  s = s.replace(/\*/g, '');
  if (!s) return { valid: false };

  const xIdx = s.indexOf('x');
  if (xIdx === -1) return { valid: false };
  // Reject multiple x occurrences.
  if (s.indexOf('x', xIdx + 1) !== -1) return { valid: false };

  let bStr = s.slice(0, xIdx).trim();
  let cStr = s.slice(xIdx + 1).trim();

  // Allow forms like "pix/2 - pi/4": bStr="pi", cStr="/2-pi/4"
  // Also allow parenthesized B like "(pi/2)x" where bStr="(pi/2)".
  if (bStr.startsWith('+') || bStr.startsWith('-')) {
    const sign = bStr[0];
    const rest = bStr.slice(1).trim();
    if (rest.startsWith('(')) bStr = sign + _stripOuterParens(rest);
  } else if (bStr.startsWith('(')) {
    bStr = _stripOuterParens(bStr);
  }

  // "pix/2" case: after extracting x, bStr="pi", cStr="/2" or "/2-pi/4"
  if (bStr.endsWith('pi') && cStr.startsWith('/')) {
    const denomMatch = cStr.match(/^\/(\d+(?:\.\d+)?)/);
    if (denomMatch) {
      bStr = bStr + '/' + denomMatch[1];
      cStr = cStr.slice(denomMatch[0].length).trim();
    }
  }

  // Parse B.
  let B;
  if (bStr === '' || bStr === '+') B = 1;
  else if (bStr === '-') B = -1;
  else {
    B = parsePiNumeric(bStr);
    if (isNaN(B)) return { valid: false };
  }

  // Parse C.
  let C = 0;
  if (cStr !== '') {
    // Expect leading sign "+" or "-" for the constant term
    const cNorm = cStr.replace(/\s+/g, '');
    if (!cNorm.startsWith('+') && !cNorm.startsWith('-')) {
      return { valid: false };
    }
    C = parsePiNumeric(cNorm);
    if (isNaN(C)) return { valid: false };
  }

  return { valid: true, B, C };
};

/**
 * Parse a sine/cosine/tangent expression of the form
 *   [A] func([B]x [+ C]) [± D]
 * Supports pi in every numeric slot.
 *
 * @param {string} funcName   Lowercase function name ("sin"/"cos"/"tan")
 * @param {string} curveType  CURVE_TYPES.SINE | COSINE | TANGENT
 * @param {string} expr       RHS of y= (already lowercased, spaces stripped)
 */
const parseTrigLikeExpression = (funcName, curveType, expr) => {
  if (typeof expr !== 'string') return { valid: false };
  let s = expr.replace(/π/g, 'pi');

  // Locate the func call. Must appear exactly once; no other trig funcs.
  const funcIdx = s.indexOf(funcName + '(');
  if (funcIdx === -1) return { valid: false };
  // Reject if any other trig function name is present (prevents
  // false positives like "sin(x)+cos(x)" accidentally parsing as sine).
  for (const other of ['sin', 'cos', 'tan']) {
    if (other === funcName) continue;
    if (s.includes(other + '(')) return { valid: false };
  }

  // Find matching closing paren for funcName(...).
  const openIdx = funcIdx + funcName.length;
  if (s[openIdx] !== '(') return { valid: false };
  let depth = 0;
  let closeIdx = -1;
  for (let i = openIdx; i < s.length; i++) {
    if (s[i] === '(') depth++;
    else if (s[i] === ')') {
      depth--;
      if (depth === 0) { closeIdx = i; break; }
    }
  }
  if (closeIdx === -1) return { valid: false };

  // Split into: [before func] funcName( argument ) [after close paren]
  let beforeRaw = s.slice(0, funcIdx);
  const argStr = s.slice(openIdx + 1, closeIdx);
  let afterRaw = s.slice(closeIdx + 1);

  // Strip an optional trailing "*" after coefficient: "2*sin(x)" → before="2"
  if (beforeRaw.endsWith('*')) beforeRaw = beforeRaw.slice(0, -1);

  // --- A: leading coefficient ---
  let A;
  const beforeTrim = beforeRaw.trim();
  if (beforeTrim === '' || beforeTrim === '+') {
    A = 1;
  } else if (beforeTrim === '-') {
    A = -1;
  } else {
    A = parsePiNumeric(beforeTrim);
    if (isNaN(A)) return { valid: false };
  }

  // --- B, C from inside the parens ---
  const argParsed = parseTrigArgument(argStr);
  if (!argParsed.valid) return { valid: false };

  // --- D: vertical shift after the close paren ---
  let D = 0;
  const afterTrim = afterRaw.trim();
  if (afterTrim !== '') {
    const afterNorm = afterTrim.replace(/\s+/g, '');
    if (!afterNorm.startsWith('+') && !afterNorm.startsWith('-')) {
      return { valid: false };
    }
    D = parsePiNumeric(afterNorm);
    if (isNaN(D)) return { valid: false };
  }

  if (!isFinite(A) || !isFinite(argParsed.B) || !isFinite(argParsed.C) || !isFinite(D)) {
    return { valid: false };
  }

  return {
    valid: true,
    type: curveType,
    params: { a: A, b: argParsed.B, c: argParsed.C, d: D },
  };
};

/**
 * Parse sine expression: a * sin(bx + c) + d
 * Matches: sin(x), 2sin(x), sin(2x), sin(x)+1, 2sin(3x)-1
 * Also accepts pi-based coefficients: sin(pix/2), (1/2)sin(pix/2 - pi/4) + 1
 */
const parseSineExpression = (expr) =>
  parseTrigLikeExpression('sin', CURVE_TYPES.SINE, expr);

/**
 * Parse cosine expression: a * cos(bx + c) + d
 * Matches: cos(x), 2cos(x), cos(2x), cos(x)+1, 2cos(3x)-1
 * Also accepts pi-based coefficients.
 */
const parseCosineExpression = (expr) =>
  parseTrigLikeExpression('cos', CURVE_TYPES.COSINE, expr);

/**
 * Parse tangent expression: a * tan(bx + c) + d
 * Matches: tan(x), 2tan(x), tan(2x), tan(x)+1, 2tan(3x)-1
 * Also accepts pi-based coefficients: tan(pix/2), (1/2)tan(pix/2 - pi/4) + 1
 */
const parseTangentExpression = (expr) =>
  parseTrigLikeExpression('tan', CURVE_TYPES.TANGENT, expr);

/**
 * Evaluate a curve at a given x value
 * @param {Object} curveParams - { type, a, b, c, ... } flat structure with type and parameters
 * @param {number} x - x value to evaluate
 * @returns {number} - y value (or NaN if undefined)
 */
export const evaluateCurve = (curveParams, x) => {
  if (!curveParams || !curveParams.type) return NaN;

  const { type } = curveParams;

  switch (type) {
    case CURVE_TYPES.QUADRATIC: {
      const { a, b, c } = curveParams;
      return a * x * x + b * x + c;
    }
    case CURVE_TYPES.ABSOLUTE: {
      const { a, h, k } = curveParams;
      return a * Math.abs(x - h) + k;
    }
    case CURVE_TYPES.SQRT: {
      const { a, h, k, b = 1 } = curveParams;
      const inner = b * (x - h);
      return inner >= 0 ? a * Math.sqrt(inner) + k : NaN;
    }
    case CURVE_TYPES.NTH_ROOT: {
      const { n, a, h, k } = curveParams;
      const inner = x - h;
      if (n % 2 === 0) {
        return inner >= 0 ? a * Math.pow(inner, 1 / n) + k : NaN;
      }
      // Odd n: sign-preserving
      if (n === 3) return a * Math.cbrt(inner) + k;
      return a * Math.sign(inner) * Math.pow(Math.abs(inner), 1 / n) + k;
    }
    case CURVE_TYPES.EXPONENTIAL: {
      const { a, base, c } = curveParams;
      return a * Math.pow(base, x) + c;
    }
    case CURVE_TYPES.LOGARITHMIC: {
      const { a, base, h, k } = curveParams;
      const inner = x - h;
      if (inner <= 0) return NaN;
      return a * (Math.log(inner) / Math.log(base)) + k;
    }
    case CURVE_TYPES.SINE: {
      const { a, b, c, d } = curveParams;
      return a * Math.sin(b * x + c) + d;
    }
    case CURVE_TYPES.COSINE: {
      const { a, b, c, d } = curveParams;
      return a * Math.cos(b * x + c) + d;
    }
    case CURVE_TYPES.TANGENT: {
      const { a, b, c, d } = curveParams;
      const arg = b * x + c;
      // Near an asymptote cos(arg) → 0. Return NaN so curvesToPoints
      // emits a gap marker that the renderer uses to break the path.
      if (Math.abs(Math.cos(arg)) < 1e-4) return NaN;
      return a * Math.tan(arg) + d;
    }
    case CURVE_TYPES.POLYNOMIAL: {
      const { coefficients } = curveParams;
      if (!coefficients || coefficients.length === 0) return NaN;
      let y = 0;
      let xPower = 1;
      for (let i = 0; i < coefficients.length; i++) {
        y += coefficients[i] * xPower;
        xPower *= x;
      }
      return y;
    }
    default:
      return NaN;
  }
};

/**
 * Compute vertical-asymptote x-values for a curve within [xMin, xMax].
 * Currently only tangent has known asymptotes; other curves return [].
 * Tangent: b*x + c = π/2 + n*π  →  x = (π/2 + n*π − c) / b
 */
const curveAsymptoteXs = (curveParams, xMin, xMax) => {
  if (!curveParams || curveParams.type !== CURVE_TYPES.TANGENT) return [];
  const { b, c } = curveParams;
  if (!b || !isFinite(b)) return [];
  const pi = Math.PI;
  // Range of integer n such that the asymptote x falls inside [xMin, xMax].
  // (π/2 + n·π − c) / b ∈ [xMin, xMax]  ⇔  n ∈ [(b·xMin + c − π/2)/π, (b·xMax + c − π/2)/π]
  const lo = ((b * xMin + c) - pi / 2) / pi;
  const hi = ((b * xMax + c) - pi / 2) / pi;
  const nMin = Math.floor(Math.min(lo, hi)) - 1;
  const nMax = Math.ceil(Math.max(lo, hi)) + 1;
  const out = [];
  for (let n = nMin; n <= nMax; n++) {
    const x = (pi / 2 + n * pi - c) / b;
    if (x >= xMin - 1e-6 && x <= xMax + 1e-6) out.push(x);
  }
  return out.sort((a, b) => a - b);
};

/**
 * Generate points along a curve for rendering
 * @param {Object} curveParams - { type, params }
 * @param {Object} gridBounds - { xMin, xMax, yMin, yMax }
 * @param {number} numSamples - Number of sample points
 * @returns {Array<{x: number, y: number}>}
 */
export const curvesToPoints = (curveParams, gridBounds, numSamples = 200) => {
  const { xMin, xMax } = gridBounds;
  const points = [];
  const step = (xMax - xMin) / numSamples;

  if (!step || step <= 0) return points;

  // Base uniform sampling.
  const xValues = new Set();
  for (let x = xMin; x <= xMax; x += step) {
    xValues.add(x);
  }

  // Dense sampling near known vertical asymptotes so the rendered branch
  // reaches the grid's y-bound before the NaN gap, instead of visually
  // topping out at whatever the nearest uniform sample produced.
  const asymptoteXs = curveAsymptoteXs(curveParams, xMin, xMax);
  if (asymptoteXs.length > 0) {
    const nearDist = Math.min(step, (xMax - xMin) / 40);
    const nearStep = nearDist / 200;
    for (const ax of asymptoteXs) {
      for (let dx = -nearDist; dx <= nearDist; dx += nearStep) {
        const xv = ax + dx;
        if (xv >= xMin && xv <= xMax) xValues.add(xv);
      }
    }
  }

  const sortedX = [...xValues].sort((a, b) => a - b);
  for (const x of sortedX) {
    const y = evaluateCurve(curveParams, x);
    if (isFinite(y)) {
      points.push({ x, y });
    } else {
      // Preserve NaN / non-finite results as gap markers so the renderer
      // can break the path at discontinuities (e.g. tangent asymptotes).
      points.push({ x, y: NaN });
    }
  }

  return points;
};

/**
 * Fit a quadratic curve (y = ax^2 + bx + c) through 3 points
 * Uses Cramer's rule to solve the 3x3 system
 * @param {Array<{x: number, y: number}>} points - Exactly 3 points
 * @returns {{valid: boolean, type: string, params: Object, error: string}}
 */
export const fitQuadratic = (points) => {
  if (!points || points.length !== 3) {
    return { valid: false, error: 'Exactly 3 points required for quadratic' };
  }

  const [p1, p2, p3] = points;
  const x1 = p1.x, y1 = p1.y;
  const x2 = p2.x, y2 = p2.y;
  const x3 = p3.x, y3 = p3.y;

  // Check for duplicate x values (would make system unsolvable)
  if (x1 === x2 || x2 === x3 || x1 === x3) {
    return { valid: false, error: 'Points must have different x values' };
  }

  // Solve: [x1^2 x1 1] [a]   [y1]
  //        [x2^2 x2 1] [b] = [y2]
  //        [x3^2 x3 1] [c]   [y3]

  // Using Cramer's rule
  const det = x1*x1*(x2 - x3) - x1*(x2*x2 - x3*x3) + (x2*x2*x3 - x3*x3*x2);

  if (Math.abs(det) < 1e-10) {
    return { valid: false, error: 'Points are collinear or invalid' };
  }

  // Calculate determinants for a, b, c
  const detA = y1*(x2 - x3) - x1*(y2 - y3) + (y2*x3 - y3*x2);
  const detB = x1*x1*(y2 - y3) - y1*(x2*x2 - x3*x3) + (x2*x2*y3 - x3*x3*y2);
  const detC = x1*x1*(x2*y3 - x3*y2) - x1*(x2*x2*y3 - x3*x3*y2) + y1*(x2*x2*x3 - x3*x3*x2);

  const a = detA / det;
  const b = detB / det;
  const c = detC / det;

  // Verify it's actually a parabola (a should be non-zero for a curve)
  // But we allow a=0 which would be a line - user can decide if that's what they want

  return {
    valid: true,
    type: CURVE_TYPES.QUADRATIC,
    params: { a, b, c },
  };
};

/**
 * Fit absolute value curve (y = a|x - h| + k) from vertex + one point
 * @param {Object} vertex - The vertex point {x, y} (this is (h, k))
 * @param {Object} point - Another point on the curve {x, y}
 * @returns {{valid: boolean, type: string, params: Object, error: string}}
 */
export const fitAbsoluteValue = (vertex, point) => {
  if (!vertex || !point) {
    return { valid: false, error: 'Vertex and one point required' };
  }

  const h = vertex.x;
  const k = vertex.y;

  // Point must not be at the vertex
  if (Math.abs(point.x - h) < 1e-10) {
    return { valid: false, error: 'Second point must have different x from vertex' };
  }

  // Calculate a from: point.y = a * |point.x - h| + k
  const a = (point.y - k) / Math.abs(point.x - h);

  return {
    valid: true,
    type: CURVE_TYPES.ABSOLUTE,
    params: { a, h, k },
  };
};

/**
 * Fit square root curve (y = a*sqrt(x - h) + k) from start point + one point
 * @param {Object} start - The starting point (h, k) where curve begins
 * @param {Object} point - Another point on the curve
 * @returns {{valid: boolean, type: string, params: Object, error: string}}
 */
export const fitSquareRoot = (start, point) => {
  if (!start || !point) {
    return { valid: false, error: 'Start point and one other point required' };
  }

  const h = start.x;
  const k = start.y;

  // Second point to the right → right-opening (b=+1); to the left → left-opening (b=-1).
  // Same x is degenerate — can't solve for a.
  if (point.x === h) {
    return { valid: false, error: 'Second point must differ in x from start point' };
  }
  const b = point.x > h ? 1 : -1;

  // point.y = a * sqrt(b*(point.x - h)) + k  →  a = (point.y - k) / sqrt(b*(point.x - h))
  const sqrtVal = Math.sqrt(b * (point.x - h));
  const a = (point.y - k) / sqrtVal;

  return {
    valid: true,
    type: CURVE_TYPES.SQRT,
    params: { a, b, h, k },
  };
};

/**
 * Fit an nth-root curve y = a * ⁿ√(x - h) + k from 2 points.
 * First point anchors (h, k). Second point determines a.
 *
 * @param {{x:number,y:number}} start
 * @param {{x:number,y:number}} point
 * @param {number} n - root index, integer ≥ 2
 */
export const fitNthRoot = (start, point, n) => {
  if (!start || !point) {
    return { valid: false, error: 'Start point and one other point required' };
  }
  // Preserve fractional inputs so we can reject them (parseInt would silently truncate 2.5 → 2)
  const rootIndex = typeof n === 'number' ? n : Number(n);
  if (!Number.isInteger(rootIndex) || rootIndex < 2) {
    return { valid: false, error: 'Root index n must be an integer ≥ 2' };
  }

  const h = start.x;
  const k = start.y;
  const inner = point.x - h;

  if (rootIndex % 2 === 0 && inner < 0) {
    return { valid: false, error: 'Even roots require second point with x ≥ start x' };
  }
  if (inner === 0) {
    return { valid: false, error: 'Second point must differ in x from start point' };
  }

  let rootVal;
  if (rootIndex === 3) {
    rootVal = Math.cbrt(inner);
  } else if (rootIndex % 2 === 1) {
    rootVal = Math.sign(inner) * Math.pow(Math.abs(inner), 1 / rootIndex);
  } else {
    rootVal = Math.pow(inner, 1 / rootIndex);
  }

  const a = (point.y - k) / rootVal;

  return {
    valid: true,
    type: CURVE_TYPES.NTH_ROOT,
    params: { n: rootIndex, a, h, k },
  };
};

/**
 * Fit logarithmic curve (y = a * log_base(x - h) + k) from 2 points.
 * First point is the anchor where log argument = 1 (i.e. x1 - h = 1, so log = 0 and y1 = k).
 * Second point defines the stretch factor a.
 * @param {Object} anchor - The anchor point {x, y} where log = 0, so h = x - 1 and k = y
 * @param {Object} point - Another point on the curve {x, y}
 * @param {number} base - The logarithm base (default 10)
 * @returns {{valid: boolean, type: string, params: Object, error: string}}
 */
export const fitLogarithmic = (anchor, point, base = 10) => {
  if (!anchor || !point) {
    return { valid: false, error: 'Two points required' };
  }

  if (base <= 0 || base === 1) {
    return { valid: false, error: 'Base must be positive and not equal to 1' };
  }

  // anchor is where log(x - h) = 0, meaning x - h = 1, so h = x - 1
  const h = anchor.x - 1;
  const k = anchor.y;

  // Second point must satisfy x - h > 0
  const inner = point.x - h;
  if (inner <= 0) {
    return { valid: false, error: 'Second point must be to the right of the vertical asymptote (x > h)' };
  }

  // x - h must not equal 1 (log(1) = 0 gives no info about a)
  const logVal = Math.log(inner) / Math.log(base);
  if (Math.abs(logVal) < 1e-10) {
    return { valid: false, error: 'Second point must have a different x from the anchor' };
  }

  const a = (point.y - k) / logVal;

  if (!isFinite(a) || isNaN(a)) {
    return { valid: false, error: 'Could not fit logarithmic curve' };
  }

  return {
    valid: true,
    type: CURVE_TYPES.LOGARITHMIC,
    params: { a, base, h, k },
  };
};

/**
 * Fit exponential curve (y = a * b^x + c) from 3 equally-spaced points
 * @param {Object} p1 - First point
 * @param {Object} p2 - Second point (x-spacing must equal p1→p2 and p2→p3)
 * @param {Object} p3 - Third point
 * @returns {{valid: boolean, type: string, params: Object, error: string}}
 */
export const fitExponential = (p1, p2, p3) => {
  if (!p1 || !p2 || !p3) {
    return { valid: false, error: 'Three points required' };
  }

  const d1 = p2.x - p1.x;
  const d2 = p3.x - p2.x;

  if (Math.abs(d1) < 1e-10 || Math.abs(d2) < 1e-10) {
    return { valid: false, error: 'Points must have different x values' };
  }

  if (Math.abs(d1 - d2) > 0.01) {
    return { valid: false, error: 'Points must have equal x-spacing' };
  }

  const d = d1;
  const { y: y1 } = p1;
  const { y: y2 } = p2;
  const { y: y3 } = p3;

  // For equally spaced x: c = (y1*y3 - y2^2) / (y1 + y3 - 2*y2)
  const denom = y1 + y3 - 2 * y2;
  let c = 0;
  if (Math.abs(denom) > 1e-10) {
    c = (y1 * y3 - y2 * y2) / denom;
  }

  const s2 = y2 - c;
  const s3 = y3 - c;

  if (Math.abs(s2) < 1e-10) {
    return { valid: false, error: 'Could not fit exponential curve' };
  }

  // s2 and s3 must have the same sign
  if (s2 * s3 <= 0) {
    return { valid: false, error: 'Could not fit exponential curve' };
  }

  const ratio = s3 / s2;
  const base = Math.pow(Math.abs(ratio), 1 / d);
  const a = s2 / Math.pow(base, p2.x);

  if (isNaN(base) || isNaN(a) || !isFinite(base) || !isFinite(a) || base <= 0) {
    return { valid: false, error: 'Could not fit exponential curve' };
  }

  return {
    valid: true,
    type: CURVE_TYPES.EXPONENTIAL,
    params: { a, base, c },
  };
};

/**
 * Fit sine curve from 3 key points (max, min, and a zero crossing)
 * Simplified: fits y = a*sin(bx) + d
 * @param {Object} maxPoint - A maximum point
 * @param {Object} minPoint - A minimum point
 * @param {Object} zeroPoint - A point where curve crosses midline
 * @returns {{valid: boolean, type: string, params: Object, error: string}}
 */
export const fitSine = (maxPoint, minPoint, zeroPoint) => {
  if (!maxPoint || !minPoint || !zeroPoint) {
    return { valid: false, error: 'Max, min, and zero crossing points required' };
  }

  // Amplitude = (max - min) / 2
  const amplitude = (maxPoint.y - minPoint.y) / 2;

  // Vertical shift = (max + min) / 2
  const d = (maxPoint.y + minPoint.y) / 2;

  // Period = 2 * |maxX - minX| (distance from max to min is half period)
  const halfPeriod = Math.abs(maxPoint.x - minPoint.x);
  const period = 2 * halfPeriod;

  // b = 2π / period
  const b = (2 * Math.PI) / period;

  // Phase shift from zero crossing
  // At zero crossing going up, sin(bx + c) = 0, so bx + c = 0, c = -bx
  const c = -b * zeroPoint.x;

  return {
    valid: true,
    type: CURVE_TYPES.SINE,
    params: { a: amplitude, b, c, d },
  };
};

/**
 * Fit cosine curve from 3 key points
 * Similar to sine fitting
 */
export const fitCosine = (maxPoint, minPoint, zeroPoint) => {
  if (!maxPoint || !minPoint || !zeroPoint) {
    return { valid: false, error: 'Max, min, and zero crossing points required' };
  }

  const amplitude = (maxPoint.y - minPoint.y) / 2;
  const d = (maxPoint.y + minPoint.y) / 2;
  const halfPeriod = Math.abs(maxPoint.x - minPoint.x);
  const period = 2 * halfPeriod;
  const b = (2 * Math.PI) / period;

  // For cosine, max occurs at bx + c = 0, so c = -b * maxX
  const c = -b * maxPoint.x;

  return {
    valid: true,
    type: CURVE_TYPES.COSINE,
    params: { a: amplitude, b, c, d },
  };
};

/**
 * Fit tangent curve y = a·tan(bx + c) + d from 3 clicks.
 *
 * Click order:
 *   1. midlinePoint     — any point where the curve crosses its midline (y = d)
 *   2. asymptotePoint   — any point on a neighboring vertical asymptote (only x matters)
 *   3. thirdPoint       — one more point on the curve, away from midline/asymptote
 *
 * Derivation:
 *   From midline:  tan(B·xm + C) = 0  ⇒  C = −B·xm  and  D = ym
 *   From asymptote: B·xa + C = ±π/2  ⇒  B = π / (2·(xa − xm))
 *   From third:    A = (y3 − D) / tan(B·x3 + C)
 */
export const fitTangent = (midlinePoint, asymptotePoint, thirdPoint) => {
  if (!midlinePoint || !asymptotePoint || !thirdPoint) {
    return { valid: false, error: 'Midline, asymptote, and third point required' };
  }

  const dx = asymptotePoint.x - midlinePoint.x;
  if (Math.abs(dx) < 1e-6) {
    return { valid: false, error: 'Asymptote must differ in x from the midline point' };
  }

  const d = midlinePoint.y;
  const b = Math.PI / (2 * dx);
  const c = -b * midlinePoint.x;

  const arg3 = b * thirdPoint.x + c;
  const tan3 = Math.tan(arg3);
  if (!isFinite(tan3) || Math.abs(tan3) < 1e-6) {
    return {
      valid: false,
      error: 'Third point is too close to the midline — move it further away',
    };
  }

  const a = (thirdPoint.y - d) / tan3;
  if (!isFinite(a)) {
    return { valid: false, error: 'Could not determine amplitude from third point' };
  }

  return {
    valid: true,
    type: CURVE_TYPES.TANGENT,
    params: { a, b, c, d },
  };
};

/**
 * Normalize tangent (a, b, c, d) into a canonical form so mathematically
 * equivalent curves compare equal.
 *
 * Identities used:
 *   tan(−θ) = −tan(θ)        — making B positive requires negating C and A
 *   tan(θ + π) = tan(θ)      — C is only determined mod π (period π)
 *
 * Canonical form: B > 0, C ∈ [0, π).
 */
const normalizeTanParams = ({ a, b, c, d }) => {
  let A = a;
  let B = b;
  let C = c;
  if (B < 0) {
    // A·tan(−Bx − C) = A·(−tan(Bx + C)) = −A·tan(Bx + C),
    // so flipping B to positive is equivalent to negating both C and A.
    B = -B;
    C = -C;
    A = -A;
  }
  // Reduce C modulo π into [0, π). No A flip — tan is π-periodic.
  const pi = Math.PI;
  C = ((C % pi) + pi) % pi;
  return { a: A, b: B, c: C, d };
};

/**
 * Check if two curves are mathematically equivalent
 * @param {Object} curve1 - { type, a, b, c, ... } flat structure
 * @param {Object} curve2 - { type, a, b, c, ... } flat structure
 * @param {number} tolerance - Comparison tolerance
 * @returns {boolean}
 */
export const curvesEquivalent = (curve1, curve2, tolerance = 0.01) => {
  if (!curve1 || !curve2) return false;
  if (curve1.type !== curve2.type) return false;

  const floatEq = (a, b) => Math.abs(a - b) <= tolerance;

  switch (curve1.type) {
    case CURVE_TYPES.QUADRATIC:
      return floatEq(curve1.a, curve2.a) && floatEq(curve1.b, curve2.b) && floatEq(curve1.c, curve2.c);

    case CURVE_TYPES.ABSOLUTE:
      return floatEq(curve1.a, curve2.a) && floatEq(curve1.h, curve2.h) && floatEq(curve1.k, curve2.k);

    case CURVE_TYPES.SQRT:
      return floatEq(curve1.a, curve2.a) && floatEq(curve1.h, curve2.h) && floatEq(curve1.k, curve2.k)
        && ((curve1.b ?? 1) === (curve2.b ?? 1));

    case CURVE_TYPES.NTH_ROOT:
      return curve1.n === curve2.n &&
             floatEq(curve1.a, curve2.a) && floatEq(curve1.h, curve2.h) && floatEq(curve1.k, curve2.k);

    case CURVE_TYPES.EXPONENTIAL:
      return floatEq(curve1.a, curve2.a) && floatEq(curve1.base, curve2.base) && floatEq(curve1.c, curve2.c);

    case CURVE_TYPES.LOGARITHMIC:
      return floatEq(curve1.a, curve2.a) && floatEq(curve1.base, curve2.base) &&
             floatEq(curve1.h, curve2.h) && floatEq(curve1.k, curve2.k);

    case CURVE_TYPES.SINE:
    case CURVE_TYPES.COSINE:
      return floatEq(curve1.a, curve2.a) && floatEq(curve1.b, curve2.b) &&
             floatEq(curve1.c, curve2.c) && floatEq(curve1.d, curve2.d);

    case CURVE_TYPES.TANGENT: {
      // Normalize into canonical form before comparing so that equivalent
      // but differently-parameterized tangents (e.g. (A,C) vs (−A, C+π)) match.
      const n1 = normalizeTanParams(curve1);
      const n2 = normalizeTanParams(curve2);
      return floatEq(n1.a, n2.a) && floatEq(n1.b, n2.b) &&
             floatEq(n1.c, n2.c) && floatEq(n1.d, n2.d);
    }

    case CURVE_TYPES.POLYNOMIAL: {
      // Must have same degree
      if (curve1.degree !== curve2.degree) return false;

      const c1 = curve1.coefficients;
      const c2 = curve2.coefficients;

      if (!c1 || !c2 || c1.length !== c2.length) return false;

      // Compare all coefficients
      return c1.every((coef, i) => floatEq(coef, c2[i]));
    }

    default:
      return false;
  }
};

/**
 * Check if a point lies on a curve (within tolerance)
 * @param {Object} point - { x, y }
 * @param {Object} curveParams - { type, params }
 * @param {number} tolerance - How close y must be
 * @returns {boolean}
 */
export const pointOnCurve = (point, curveParams, tolerance = 0.1) => {
  const expectedY = evaluateCurve(curveParams, point.x);
  if (isNaN(expectedY)) return false;
  return Math.abs(point.y - expectedY) <= tolerance;
};

/**
 * Format coordinate answer for display
 * Shared utility for rendering coordinate answers consistently across views
 * @param {Array|string} answer - The coordinate answer (can be array with JSON string or direct JSON string)
 * @param {Object} config - Answer configuration containing grid settings
 * @param {number} gridSize - Size of the coordinate grid (width/height), defaults to 150
 * @returns {Object|null} - Object with parsed elements and gridConfig, or null if invalid
 */
export const parseCoordinateAnswerForDisplay = (answer, config, gridSize = 150) => {
  let elements = [];
  try {
    if (answer && Array.isArray(answer) && answer[0]) {
      const parsed = JSON.parse(answer[0]);
      elements = parsed?.elements || [];
    } else if (answer && typeof answer === 'string') {
      const parsed = JSON.parse(answer);
      elements = parsed?.elements || [];
    }
  } catch (e) {
    return null;
  }

  if (elements.length === 0) return null;

  const gridConfig = config?.grid || defaultCoordinateConfig.grid;

  // Hydrate curve elements: regenerate rendering points from parsed data
  const hydratedElements = elements.map(el => {
    if (el.type === 'curve' && (!el.points || el.points.length < 2) && el.parsed) {
      return { ...el, points: curvesToPoints(el.parsed, gridConfig) };
    }
    return el;
  });

  return {
    elements: hydratedElements,
    gridConfig,
    gridSize
  };
};

/**
 * Generate a description of answer validation logic for coordinate grid questions
 * This helps admins and students understand how answers are evaluated
 * @param {Array} elements - The correct answer elements
 * @param {Object} config - The answer configuration
 * @returns {Object} - Description object with tolerance info and per-element rules
 */
export const generateAnswerLogicDescription = (elements, config) => {
  const tolerance = config?.tolerance || 0.01;
  const precision = config?.precision || 2;
  const curveTolerance = tolerance * 10; // Curves use 10x tolerance

  const result = {
    tolerance,
    precision,
    curveTolerance,
    toleranceDescription: `Tolerance: ${tolerance} units | Precision: ${precision} decimal places`,
    elements: [],
  };

  if (!elements || !Array.isArray(elements) || elements.length === 0) {
    return result;
  }

  for (const el of elements) {
    const elementInfo = {
      id: el.id,
      type: el.type,
      label: formatElementSummary(el),
      rules: [],
      equivalentForms: [],
    };

    switch (el.type) {
      case 'point':
        elementInfo.rules.push(`Must be within ${tolerance} units of correct position`);
        break;

      case 'line':
      case 'equationLine':
        elementInfo.rules.push('Any 2 points on the line accepted');
        elementInfo.rules.push('Compared by slope-intercept form');
        elementInfo.rules.push('Angular comparison used for steep lines');
        // Generate equivalent forms for lines
        if (el.equation) {
          const equivalents = generateLineEquivalentForms(el);
          elementInfo.equivalentForms = equivalents;
        } else if (el.points && el.points.length >= 2) {
          const slopeInt = lineToSlopeIntercept(el.points[0], el.points[1]);
          if (!slopeInt.isVertical) {
            const m = slopeInt.slope;
            const b = slopeInt.intercept;
            elementInfo.equivalentForms.push(`y = ${formatSlopeInterceptEquation(m, b)}`);
            // Standard form: ax + by = c
            const standardForm = generateStandardForm(m, b);
            if (standardForm) elementInfo.equivalentForms.push(standardForm);
          } else {
            elementInfo.equivalentForms.push(`x = ${decimalToFraction(slopeInt.verticalX)}`);
          }
        }
        break;

      case 'polygon':
        elementInfo.rules.push(`${el.points?.length || 0} vertices must match`);
        elementInfo.rules.push('Rotation and reflection accepted');
        elementInfo.rules.push('Vertex order can be reversed');
        break;

      case 'circle':
        elementInfo.rules.push(`Center within ${tolerance} units`);
        elementInfo.rules.push(`Radius within ${tolerance} units`);
        break;

      case 'curve':
        elementInfo.rules.push(`Parameters compared with ${curveTolerance} tolerance (10x standard)`);
        elementInfo.rules.push('Curve type must match exactly');
        if (el.curveType) {
          elementInfo.rules.push(`Curve type: ${el.curveType}`);
        }
        // Add equivalent forms for curves
        if (el.equation) {
          elementInfo.equivalentForms.push(el.equation);
        }
        break;

      default:
        elementInfo.rules.push('Standard comparison rules apply');
    }

    result.elements.push(elementInfo);
  }

  return result;
};

/**
 * Generate equivalent forms for a line equation
 * @param {Object} lineElement - Line element with equation or parsed data
 * @returns {Array<string>} - Array of equivalent equation forms
 */
const generateLineEquivalentForms = (lineElement) => {
  const forms = [];

  if (lineElement.equation) {
    forms.push(lineElement.equation);
  }

  // Get slope-intercept form
  const slopeInt = elementToSlopeIntercept(lineElement);
  if (!slopeInt) return forms;

  if (slopeInt.isVertical) {
    forms.push(`x = ${decimalToFraction(slopeInt.verticalX)}`);
    return [...new Set(forms)]; // Remove duplicates
  }

  const m = slopeInt.slope;
  const b = slopeInt.intercept;

  // Slope-intercept form: y = mx + b
  const slopeInterceptForm = `y = ${formatSlopeInterceptEquation(m, b)}`;
  if (!forms.includes(slopeInterceptForm)) {
    forms.push(slopeInterceptForm);
  }

  // Standard form: ax + by = c
  const standardForm = generateStandardForm(m, b);
  if (standardForm && !forms.includes(standardForm)) {
    forms.push(standardForm);
  }

  return [...new Set(forms)]; // Remove duplicates
};

/**
 * Format slope-intercept equation string
 * @param {number} m - slope
 * @param {number} b - y-intercept
 * @returns {string}
 */
const formatSlopeInterceptEquation = (m, b) => {
  const mStr = decimalToFraction(m);
  const bStr = decimalToFraction(Math.abs(b));

  if (Math.abs(m) < 1e-10) {
    return decimalToFraction(b);
  }

  let result = '';
  if (Math.abs(m - 1) < 1e-10) {
    result = 'x';
  } else if (Math.abs(m + 1) < 1e-10) {
    result = '-x';
  } else {
    result = `${mStr}x`;
  }

  if (Math.abs(b) > 1e-10) {
    result += b > 0 ? ` + ${bStr}` : ` - ${bStr}`;
  }

  return result;
};

/**
 * Generate standard form (ax + by = c) from slope-intercept
 * @param {number} m - slope
 * @param {number} b - y-intercept
 * @returns {string|null}
 */
const generateStandardForm = (m, b) => {
  // y = mx + b => -mx + y = b => mx - y = -b
  // We want integer coefficients when possible
  const a = -m;
  const c = b;

  // Format: ax + by = c
  const aStr = Math.abs(a - 1) < 1e-10 ? '' :
               Math.abs(a + 1) < 1e-10 ? '-' :
               decimalToFraction(a);
  const cStr = decimalToFraction(c);

  if (Math.abs(a) < 1e-10) {
    return `y = ${cStr}`;
  }

  return `${aStr}x + y = ${cStr}`;
};

/**
 * Generate general comparison rules for coordinate grid questions
 * Used in preview mode to show students how their answers will be graded
 * without revealing specific answer details
 * @param {Object} config - The answer configuration
 * @param {Array<string>} allowedModes - Array of allowed element types
 * @returns {Array<{type: string, label: string, rules: Array<string>}>}
 */
export const generateGeneralComparisonRules = (config, allowedModes = []) => {
  const tolerance = config?.tolerance || 0.01;
  const curveTolerance = tolerance * 10;
  const rules = [];

  // Map mode names to display names
  const modeLabels = {
    point: 'Points',
    line: 'Lines',
    polygon: 'Polygons',
    circle: 'Circles',
    quadratic: 'Quadratic Curves',
    absolute: 'Absolute Value Curves',
    sqrt: 'Square Root Curves',
    nthRoot: 'Nth Root Curves',
    exponential: 'Exponential Curves',
    logarithmic: 'Logarithmic Curves',
    sine: 'Sine Curves',
    cosine: 'Cosine Curves',
    tangent: 'Tangent Curves',
    polynomial2: 'Polynomial Curves (Degree 2)',
    polynomial3: 'Polynomial Curves (Degree 3)',
    polynomial4: 'Polynomial Curves (Degree 4)',
    polynomial5: 'Polynomial Curves (Degree 5)',
  };

  // Curve types that use the same tolerance rules
  const curveTypes = ['quadratic', 'absolute', 'sqrt', 'nthRoot', 'exponential', 'logarithmic', 'sine', 'cosine', 'tangent', 'polynomial2', 'polynomial3', 'polynomial4', 'polynomial5'];

  for (const mode of allowedModes) {
    const ruleEntry = {
      type: mode,
      label: modeLabels[mode] || mode,
      rules: [],
    };

    switch (mode) {
      case 'point':
        ruleEntry.rules.push(`Must be within ${tolerance} units of correct position`);
        break;

      case 'line':
        ruleEntry.rules.push('Any 2 points defining the same line are accepted');
        ruleEntry.rules.push('Compared using slope-intercept form');
        ruleEntry.rules.push(`Slope and intercept must match within ${tolerance} tolerance`);
        break;

      case 'polygon':
        ruleEntry.rules.push('All vertices must match correct positions');
        ruleEntry.rules.push('Rotation and reflection of vertices accepted');
        ruleEntry.rules.push('Vertex order can be clockwise or counter-clockwise');
        break;

      case 'circle':
        ruleEntry.rules.push(`Center must be within ${tolerance} units`);
        ruleEntry.rules.push(`Radius must be within ${tolerance} units`);
        break;

      default:
        // Check if it's a curve type
        if (curveTypes.includes(mode)) {
          ruleEntry.rules.push(`Parameters compared with ${curveTolerance} tolerance (10x standard)`);
          ruleEntry.rules.push('Curve type must match exactly');
          ruleEntry.rules.push('Equation form can vary (e.g., vertex form, standard form)');
        }
        break;
    }

    if (ruleEntry.rules.length > 0) {
      rules.push(ruleEntry);
    }
  }

  return rules;
};

/**
 * Build a human-readable equation string from curve parsed parameters.
 * Used as fallback when curve elements are missing the `equation` field
 * (e.g., from AI extraction which may not include it).
 */
const buildCurveEquationString = (curveType, parsed) => {
  if (!curveType || !parsed) return '(unknown curve)';
  const fmt = (n) => {
    if (n == null) return '?';
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };
  switch (curveType) {
    case 'quadratic':
      return `y = ${fmt(parsed.a)}x² + ${fmt(parsed.b)}x + ${fmt(parsed.c)}`;
    case 'exponential':
      return `y = ${fmt(parsed.a)} × ${fmt(parsed.base)}^x + ${fmt(parsed.c)}`;
    case 'absolute':
      return `y = ${fmt(parsed.a)}|x - ${fmt(parsed.h)}| + ${fmt(parsed.k)}`;
    case 'sqrt': {
      const bVal = parsed.b ?? 1;
      const inside = bVal < 0 ? `${fmt(parsed.h)} - x` : `x - ${fmt(parsed.h)}`;
      return `y = ${fmt(parsed.a)}√(${inside}) + ${fmt(parsed.k)}`;
    }
    case 'nthRoot': {
      if (parsed.n === 3) {
        return `y = ${fmt(parsed.a)}·∛(x - ${fmt(parsed.h)}) + ${fmt(parsed.k)}`;
      }
      return `y = ${fmt(parsed.a)}·root(${parsed.n}, x - ${fmt(parsed.h)}) + ${fmt(parsed.k)}`;
    }
    case 'logarithmic': {
      const logBaseLabel = Math.abs(parsed.base - Math.E) < 1e-10 ? 'ln' : `log_${Number.isInteger(parsed.base) ? parsed.base : fmt(parsed.base)}`;
      return `y = ${fmt(parsed.a)}${logBaseLabel}(x - ${fmt(parsed.h)}) + ${fmt(parsed.k)}`;
    }
    case 'sine':
      return `y = ${fmt(parsed.a)}sin(${fmt(parsed.b)}x + ${fmt(parsed.c)}) + ${fmt(parsed.d)}`;
    case 'cosine':
      return `y = ${fmt(parsed.a)}cos(${fmt(parsed.b)}x + ${fmt(parsed.c)}) + ${fmt(parsed.d)}`;
    case 'tangent':
      return `y = ${fmt(parsed.a)}tan(${fmt(parsed.b)}x + ${fmt(parsed.c)}) + ${fmt(parsed.d)}`;
    case 'polynomial':
      if (parsed.coefficients && Array.isArray(parsed.coefficients)) {
        const terms = parsed.coefficients.map((c, i) => {
          if (c === 0) return null;
          if (i === 0) return fmt(c);
          const coeff = c === 1 ? '' : c === -1 ? '-' : fmt(c);
          const power = i === 1 ? 'x' : `x^${i}`;
          return `${coeff}${power}`;
        }).filter(Boolean).reverse();
        return `y = ${terms.join(' + ') || '0'}`;
      }
      return `y = polynomial`;
    default:
      return `y = ${curveType}(...)`;
  }
};

/**
 * Format element summary for display in chips/badges
 * Handles all element types including edge cases where type may be missing or unexpected
 * @param {Object} el - The element to format
 * @param {Function} formatCoordinateFn - Function to format coordinates (optional, uses formatCoordinate by default)
 * @param {Function} decimalToFractionFn - Function to convert decimals to fractions (optional, uses decimalToFraction by default)
 * @returns {string} - Human-readable summary of the element
 */
export const formatElementSummary = (el, formatCoordinateFn = formatCoordinate, decimalToFractionFn = decimalToFraction) => {
  if (!el || !el.type) {
    return 'Unknown element';
  }

  switch (el.type) {
    case 'point':
      return `Point ${formatCoordinateFn(el.x, el.y, el.xLabel, el.yLabel)}`;
    case 'line':
      if (el.equation) {
        // Line created with equation but has type 'line' instead of 'equationLine'
        return `Line: ${el.equation}`;
      }
      if (el.points && el.points.length >= 2) {
        return `Line: ${formatCoordinateFn(el.points[0].x, el.points[0].y, el.points[0].xLabel, el.points[0].yLabel)} → ${formatCoordinateFn(el.points[1].x, el.points[1].y, el.points[1].xLabel, el.points[1].yLabel)}`;
      }
      return 'Line: (invalid)';
    case 'equationLine':
      return `Line: ${el.equation}`;
    case 'polygon':
      return `Polygon (${el.points?.length || 0} vertices)`;
    case 'circle':
      return `Circle: center ${formatCoordinateFn(el.center?.x, el.center?.y, el.center?.xLabel, el.center?.yLabel)}, r=${pickLabel(el.radiusLabel, el.radius, decimalToFractionFn)}`;
    case 'curve':
      return `Curve: ${el.equation || buildCurveEquationString(el.curveType, el.parsed)}`;
    default:
      // Fallback: try to display something useful based on available properties
      if (el.equation) {
        return `Line: ${el.equation}`;
      }
      if (el.points && el.points.length === 2) {
        return `Line: ${formatCoordinateFn(el.points[0].x, el.points[0].y)} → ${formatCoordinateFn(el.points[1].x, el.points[1].y)}`;
      }
      return `Unknown (${el.type || 'no type'})`;
  }
};
